/**
 * client/pixi-renderer.ts
 * Responsabilidad: Motor de renderizado acelerado por GPU sobre WebGL usando PixiJS (v8).
 *
 * Mejoras Visuales de Alta Gama (Fidelidad AAA):
 * 1. Eliminación total de aros y discos concéntricos oscuros alrededor de los jugadores.
 * 2. Restauración del fondo cósmico: 3 nebulosas cósmicas gigantes, círculo de transmutación y 45 motas de energía ambiental con parallax 2.5D.
 * 3. Rediseño del elemento fuego: silueta vectorial de bola de fuego (flame burst) con 18-22 lenguas curvadas lamiendo el perímetro, gradiente amarillo/naranja/rojo y respiración sinusoidal.
 * 4. Efecto de propulsor alquímico (isBoosting con barra espaciadora): 2 ondas de choque elípticas y estela cónica de partículas de alta velocidad.
 * 5. Efecto de muerte visible y explosivo: shockwave grueso (5-7px) expandiéndose a 2.5x radio, flash blanco nuclear y 16 fragmentos con estela.
 * 6. Microinteracciones de proximidad hiper-visibles a <= 85px: relámpagos bifurcados gruesos (3-4px), nubes de vapor térmico de 15-20px, zarcillos neón y chispas de combustión.
 * - Cero recolección de basura por cuadro a 60 FPS estables.
 */

import { Application, Container, Graphics, Sprite, Text, Texture } from "pixi.js";
import { Camera } from "./camera";
import {
    MAP_WIDTH,
    MAP_HEIGHT,
    ARENA_CORNER_RADIUS,
    DEATH_ANIMATION_MS,
    POWERUP_CONFIG,
    POWERUP_RADIUS,
    METEOR_IMPACT_RADIUS,
    METEOR_WARNING_MS,
    EMOTE_CATALOG,
} from "../src/shared/constants";
import type {
    RenderablePlayer,
    DeathAnimation,
    ElementalHitEffect,
    TrailPoint,
    FoodParticle,
    RespawnRing,
    PowerUpRenderable,
    PowerUpPickupEffect,
    ClientEvent,
    MeteorRender,
    MeteorImpactEffect,
    GravityCaptureEffect,
    FloatingCombatText,
} from "./types";
import { type FoodRenderable, FOOD_RADIUS, getFamiliarImage } from "./renderer";
import {
    sanitizeCosmetics,
    getBotCosmetics,
    getElementalPalette,
    type SkinId,
    type PlayerCosmeticsConfig,
    type AuraStyle,
    type ThrusterStyle,
} from "./skins";
import { hashString } from "./utils";
import { drawPlayerTitleHologram, syncTitlesCatalogFromServer } from "./hologram-titles";
import { FamiliarParticleSystem, getFamiliarTargetPosition, getFamiliarElement } from "./familiars";
import { getDefaultSkinForElement } from "./element-skins";
import { drawPixiElementSkin } from "./element-skins-renderer";

/** Caché en memoria de texturas PixiJS para los 15 familiares */
const familiarTextureCache = new Map<string, Texture>();
function getFamiliarTexture(id: string): Texture {
    let tex = familiarTextureCache.get(id);
    if (!tex) {
        const famImg = getFamiliarImage(id);
        if (famImg && famImg.complete && famImg.naturalWidth > 0) {
            tex = Texture.from(famImg);
            familiarTextureCache.set(id, tex);
        } else if (famImg) {
            tex = Texture.from(famImg);
            familiarTextureCache.set(id, tex);
            famImg.onload = () => {
                familiarTextureCache.set(id, Texture.from(famImg));
            };
        } else {
            tex = Texture.from(`assets/familiars/${id}.png`);
            familiarTextureCache.set(id, tex);
        }
    }
    return tex;
}

/** Paleta de nutrientes por forma elemental (0=Fuego, 1=Agua, 2=Planta, 3=Eléctrico, 4=Tierra) */
const FOOD_HEX_COLORS: number[] = [
    0xef4444, // Brasa Ígnea
    0x38bdf8, // Gota de Rocío
    0x22c55e, // Semilla Floral
    0xfacc15, // Chispa Voltáica
    0xd97706, // Fragmento Mineral
];

/** Paleta de colores elementales en entero hexadecimal */
const ELEMENT_HEX_COLORS: Record<string, number> = {
    fire: 0xef4444,
    water: 0x3b82f6,
    plant: 0x22c55e,
    electric: 0xeab308,
    earth: 0xa16207,
};

/** Colores enteros hexadecimales por tipo de impacto elemental */
const ELEMENTAL_HIT_HEX: Record<string, number> = {
    "water->fire": 0x3b82f6,
    "fire->plant": 0xef4444,
    "plant->earth": 0x22c55e,
    "earth->electric": 0xa16207,
    "electric->water": 0xeab308,
    "chain-electric": 0xfacc15,
    "shield-break": 0x3b82f6,
    "freeze-burst": 0xa5f3fc,
};

/** Colores para los 5 power-ups */
const POWERUP_HEX_COLORS: Record<string, number> = {
    invulnerability: 0xfbbf24,
    speed: 0x38bdf8,
    shield: 0x3b82f6,
    "double-mass": 0x10b981,
    freeze: 0xa5f3fc,
};

/** Mapeo de colores para eventos globales */
const EVENT_HEX_COLORS: Record<string, number> = {
    "poison-zone": 0x22c55e,
    "gravity-well": 0xa855f7,
    "meteor-shower": 0xef4444,
    "food-rain": 0xf59e0b,
};

/** Estructura interna de partícula de microinteracción de proximidad */
interface ProximityParticle {
    x: number;
    y: number;
    vx: number;
    vy: number;
    radius: number;
    kind: "steam" | "root-bloom" | "ember-spark";
    birthTime: number;
    duration: number;
    inUse: boolean;
}

/**
 * Convierte cualquier string de color CSS/hex a entero numérico PixiJS de forma segura.
 */
function parseColorToHex(col: string | undefined, defaultHex: number): number {
    if (!col) return defaultHex;
    if (col.startsWith("#")) {
        const hex = parseInt(col.slice(1), 16);
        return Number.isNaN(hex) ? defaultHex : hex;
    }
    return defaultHex;
}

/**
 * Contenedor visual reciclable por entidad de jugador/bot.
 */
class PlayerEntityView {
    public readonly container: Container;
    public readonly thrusterGraphics: Graphics;
    public readonly auraGraphics: Graphics;
    public readonly bodyGraphics: Graphics;
    public readonly elementSkinAura: Graphics;
    public readonly elementSkinSprite: Sprite;
    public readonly cosmeticsGraphics: Graphics;
    public readonly statusGraphics: Graphics;
    public readonly aliasText: Text;
    public readonly botBadgeText: Text;
    public readonly titleHologramGraphics: Graphics;
    public readonly titleText: Text;
    public readonly emoteContainer: Container;
    public readonly emoteBubbleGraphics: Graphics;
    public readonly emoteText: Text;
    public familiarContainer: Container;
    public familiarSprite: Sprite;
    public familiarTrailGraphics: Graphics;
    public familiarParticles: FamiliarParticleSystem;
    public familiarCurX: number = 0;
    public familiarCurY: number = 0;
    public familiarInitialized: boolean = false;
    public currentFamiliarId: string = "";
    public inUse: boolean = false;

    constructor() {
        this.container = new Container();

        this.thrusterGraphics = new Graphics();
        this.auraGraphics = new Graphics();
        this.bodyGraphics = new Graphics();
        this.elementSkinAura = new Graphics();
        this.elementSkinSprite = new Sprite();
        this.elementSkinSprite.anchor.set(0.5, 0.5);
        this.cosmeticsGraphics = new Graphics();
        this.statusGraphics = new Graphics();

        this.container.addChild(this.thrusterGraphics);
        this.container.addChild(this.auraGraphics);
        this.container.addChild(this.bodyGraphics);
        this.container.addChild(this.elementSkinAura);
        this.container.addChild(this.elementSkinSprite);
        this.container.addChild(this.cosmeticsGraphics);
        this.container.addChild(this.statusGraphics);

        this.familiarContainer = new Container();
        this.familiarTrailGraphics = new Graphics();
        this.familiarSprite = new Sprite();
        this.familiarSprite.anchor.set(0.5, 0.5);
        this.familiarParticles = new FamiliarParticleSystem();

        this.familiarContainer.addChild(this.familiarTrailGraphics);
        this.familiarContainer.addChild(this.familiarSprite);
        this.familiarContainer.visible = false;
        this.container.addChild(this.familiarContainer);

        this.aliasText = new Text({
            text: "",
            style: {
                fontFamily: "system-ui, sans-serif",
                fontSize: 12,
                fontWeight: "bold",
                fill: 0xffffff,
                stroke: { color: 0x000000, width: 3 },
                align: "center",
            },
        });
        this.aliasText.anchor.set(0.5, 1);

        this.botBadgeText = new Text({
            text: "[BOT]",
            style: {
                fontFamily: "system-ui, sans-serif",
                fontSize: 9,
                fontWeight: "bold",
                fill: 0x93c5fd,
                stroke: { color: 0x1e3a8a, width: 2 },
                align: "center",
            },
        });
        this.botBadgeText.anchor.set(0.5, 0);

        this.titleHologramGraphics = new Graphics();
        this.titleText = new Text({
            text: "",
            style: {
                fontFamily: "system-ui, sans-serif",
                fontSize: 10.5,
                fontWeight: "900",
                letterSpacing: 0.6,
                fill: 0xfef08a,
                stroke: { color: 0x050811, width: 3.5 },
                align: "center",
            },
        });
        this.titleText.anchor.set(0.5, 0.5);
        this.titleText.visible = false;

        this.emoteContainer = new Container();
        this.emoteBubbleGraphics = new Graphics();
        this.emoteText = new Text({
            text: "",
            style: {
                fontSize: 24,
                align: "center",
            },
        });
        this.emoteText.anchor.set(0.5, 0.5);
        this.emoteContainer.addChild(this.emoteBubbleGraphics);
        this.emoteContainer.addChild(this.emoteText);
        this.emoteContainer.visible = false;

        this.container.addChild(this.aliasText);
        this.container.addChild(this.botBadgeText);
        this.container.addChild(this.titleHologramGraphics);
        this.container.addChild(this.titleText);
        this.container.addChild(this.emoteContainer);
    }

    public clear(): void {
        this.thrusterGraphics.clear();
        this.auraGraphics.clear();
        this.bodyGraphics.clear();
        this.elementSkinAura.clear();
        this.elementSkinSprite.scale.set(1, 1);
        this.elementSkinSprite.visible = false;
        this.cosmeticsGraphics.clear();
        this.statusGraphics.clear();
        this.titleHologramGraphics.clear();
        this.titleText.visible = false;
        this.emoteBubbleGraphics.clear();
        this.emoteContainer.visible = false;
        this.familiarContainer.visible = false;
        this.familiarTrailGraphics.clear();
        this.familiarParticles.clear();
        this.familiarInitialized = false;
        this.currentFamiliarId = "";
        this.container.visible = false;
        this.aliasText.visible = false;
        this.botBadgeText.visible = false;
        this.inUse = false;
    }
}

/**
 * Vista de texto flotante reciclable para combos y recompensas.
 */
class FloatingTextView {
    public readonly container: Container;
    public readonly text: Text;
    public readonly subtext: Text;
    public inUse: boolean = false;

    constructor() {
        this.container = new Container();

        this.text = new Text({
            text: "",
            style: {
                fontFamily: "system-ui, sans-serif",
                fontSize: 18,
                fontWeight: "900",
                fill: 0xffffff,
                stroke: { color: 0x000000, width: 4 },
                align: "center",
            },
        });
        this.text.anchor.set(0.5, 0.5);

        this.subtext = new Text({
            text: "",
            style: {
                fontFamily: "system-ui, sans-serif",
                fontSize: 12,
                fontWeight: "700",
                fill: 0xf8fafc,
                stroke: { color: 0x000000, width: 3 },
                align: "center",
            },
        });
        this.subtext.anchor.set(0.5, 0);
        this.subtext.position.set(0, 14);

        this.container.addChild(this.text);
        this.container.addChild(this.subtext);
    }

    public clear(): void {
        this.container.visible = false;
        this.inUse = false;
    }
}

/**
 * Vista de daño flotante reciclable.
 */
class DamageTextView {
    public readonly text: Text;
    public inUse: boolean = false;

    constructor() {
        this.text = new Text({
            text: "",
            style: {
                fontFamily: "system-ui, sans-serif",
                fontSize: 16,
                fontWeight: "bold",
                fill: 0xffffff,
                stroke: { color: 0x000000, width: 3 },
                align: "center",
            },
        });
        this.text.anchor.set(0.5, 0.5);
    }

    public clear(): void {
        this.text.visible = false;
        this.inUse = false;
    }
}

/**
 * Vista de icono emoji para power-ups.
 */
class PowerUpView {
    public readonly emojiText: Text;
    public inUse: boolean = false;

    constructor() {
        this.emojiText = new Text({
            text: "",
            style: {
                fontFamily: "system-ui, sans-serif",
                fontSize: 20,
                align: "center",
            },
        });
        this.emojiText.anchor.set(0.5, 0.5);
    }

    public clear(): void {
        this.emojiText.visible = false;
        this.inUse = false;
    }
}

export class PixiRenderer {
    private readonly canvas: HTMLCanvasElement;
    private readonly camera: Camera;
    private readonly app: Application;
    private isReady: boolean = false;
    private lastViewportWidth: number = 0;
    private lastViewportHeight: number = 0;
    private mapWidth: number = MAP_WIDTH;
    private mapHeight: number = MAP_HEIGHT;

    public setMapDimensions(width: number, height: number): void {
        if (width > 0 && height > 0) {
            this.mapWidth = width;
            this.mapHeight = height;
        }
    }

    // Jerarquía de contenedores por capas
    private readonly backgroundLayer: Container;
    private readonly nebulaContainer: Container;
    private readonly eventZoneContainer: Container;
    private readonly groundLightsContainer: Container;
    private readonly trailsContainer: Container;
    private readonly foodLayer: Container;
    private readonly foodParticlesContainer: Container;
    private readonly proximityContainer: Container;
    private readonly entityLayer: Container;
    private readonly powerUpsContainer: Container;
    private readonly effectsContainer: Container;
    private readonly floatingTextsContainer: Container;
    private readonly eventHeaderContainer: Container;
    private readonly minimapContainer: Container;

    // Instancias de dibujo en lote persistentes (cero asignación en tick)
    private readonly bgGraphics: Graphics;
    private readonly eventZoneGraphics: Graphics;
    private readonly groundLightGraphics: Graphics;
    private readonly trailsGraphics: Graphics;
    private readonly foodGraphics: Graphics;
    private readonly foodParticlesGraphics: Graphics;
    private readonly proximityGraphics: Graphics;
    private readonly powerUpsGraphics: Graphics;
    private readonly effectsGraphics: Graphics;
    private readonly minimapGraphics: Graphics;

    // Nebulosas cósmicas ultra-suaves sin aros (Sprites con textura de degradado radial 512px)
    private nebulaTexture: Texture | null = null;
    private readonly nebulaSprites: Sprite[] = [];

    // Texto de alerta para el evento activo
    private readonly eventHeaderText: Text;

    // Pools de objetos de display reciclables
    private readonly entityPool: PlayerEntityView[] = [];
    private readonly floatingTextPool: FloatingTextView[] = [];
    private readonly damageTextPool: DamageTextView[] = [];
    private readonly powerUpPool: PowerUpView[] = [];

    // Pool de microinteracciones de proximidad (cero GC)
    private readonly proximityParticlesPool: ProximityParticle[] = [];
    private lastProximitySpawnAt: number = 0;
    private nextProximityIdx: number = 0;

    constructor(canvas: HTMLCanvasElement, camera: Camera) {
        this.canvas = canvas;
        this.camera = camera;

        this.app = new Application();

        // 1. Capa de Fondo (arena, transmutación y cuadrícula)
        this.backgroundLayer = new Container();
        this.bgGraphics = new Graphics();
        this.backgroundLayer.addChild(this.bgGraphics);

        // 1.1. Capa de Nebulosas Cósmicas ultra-suaves (modo de fusión aditivo/pantalla)
        this.nebulaContainer = new Container({ blendMode: "screen" });

        // 2. Capa de Zonas de Eventos Globales (bajo entidades y luces)
        this.eventZoneContainer = new Container();
        this.eventZoneGraphics = new Graphics();
        this.eventZoneContainer.addChild(this.eventZoneGraphics);

        // 3. Capa de Iluminación de Suelo para Comida (modo aditivo 'add' sobre arena oscura)
        this.groundLightsContainer = new Container({ blendMode: "add" });
        this.groundLightGraphics = new Graphics();
        this.groundLightsContainer.addChild(this.groundLightGraphics);

        // 4. Capa de Estelas (modo aditivo 'add')
        this.trailsContainer = new Container({ blendMode: "add" });
        this.trailsGraphics = new Graphics();
        this.trailsContainer.addChild(this.trailsGraphics);

        // 5. Capa de Comida (gemas rúnicas vivas)
        this.foodLayer = new Container();
        this.foodGraphics = new Graphics();
        this.foodLayer.addChild(this.foodGraphics);

        // 6. Capa de Succión de Partículas de Comida (aditivo 'add')
        this.foodParticlesContainer = new Container({ blendMode: "add" });
        this.foodParticlesGraphics = new Graphics();
        this.foodParticlesContainer.addChild(this.foodParticlesGraphics);

        // 7. Capa de Microinteracciones de Proximidad (aditivo 'add')
        this.proximityContainer = new Container({ blendMode: "add" });
        this.proximityGraphics = new Graphics();
        this.proximityContainer.addChild(this.proximityGraphics);

        // 8. Capa de Entidades de Jugadores y Bots
        this.entityLayer = new Container();

        // 9. Capa de Power-ups
        this.powerUpsContainer = new Container();
        this.powerUpsGraphics = new Graphics();
        this.powerUpsContainer.addChild(this.powerUpsGraphics);

        // 10. Capa de Efectos Especiales y Explosiones (modo aditivo 'add')
        this.effectsContainer = new Container({ blendMode: "add" });
        this.effectsGraphics = new Graphics();
        this.effectsContainer.addChild(this.effectsGraphics);

        // 11. Capa Superior de Textos Flotantes
        this.floatingTextsContainer = new Container();

        // 12. Capa de Cabecera de Evento Activo (legibilidad máxima)
        this.eventHeaderContainer = new Container();
        this.eventHeaderText = new Text({
            text: "",
            style: {
                fontFamily: "system-ui, sans-serif",
                fontSize: 22,
                fontWeight: "900",
                fill: 0xffffff,
                stroke: { color: 0x000000, width: 5 },
                align: "center",
                dropShadow: {
                    alpha: 0.8,
                    angle: Math.PI / 2,
                    blur: 6,
                    color: 0x000000,
                    distance: 3,
                },
            },
        });
        this.eventHeaderText.anchor.set(0.5, 0.5);
        this.eventHeaderText.visible = false;
        this.eventHeaderContainer.addChild(this.eventHeaderText);

        // 13. Capa de Minimapa Radar Táctico (arriba de todo, siempre visible)
        this.minimapContainer = new Container();
        this.minimapGraphics = new Graphics();
        this.minimapContainer.addChild(this.minimapGraphics);

        // Inicializar pool de partículas de proximidad fijas
        for (let i = 0; i < 120; i++) {
            this.proximityParticlesPool.push({
                x: 0,
                y: 0,
                vx: 0,
                vy: 0,
                radius: 4,
                kind: "steam",
                birthTime: 0,
                duration: 500,
                inUse: false,
            });
        }

        void this.init();
    }

    /** Genera una textura de degradado radial ultra-suave sin aros escalonados */
    private getOrCreateNebulaTexture(): Texture {
        if (!this.nebulaTexture) {
            const size = 512;
            const canvas = document.createElement("canvas");
            canvas.width = size;
            canvas.height = size;
            const ctx = canvas.getContext("2d");
            if (ctx) {
                const half = size / 2;
                const grad = ctx.createRadialGradient(half, half, 0, half, half, half);
                grad.addColorStop(0.0, "rgba(255, 255, 255, 1.0)");
                grad.addColorStop(0.2, "rgba(255, 255, 255, 0.72)");
                grad.addColorStop(0.4, "rgba(255, 255, 255, 0.38)");
                grad.addColorStop(0.65, "rgba(255, 255, 255, 0.12)");
                grad.addColorStop(0.85, "rgba(255, 255, 255, 0.03)");
                grad.addColorStop(1.0, "rgba(255, 255, 255, 0.0)");
                ctx.fillStyle = grad;
                ctx.fillRect(0, 0, size, size);
            }
            this.nebulaTexture = Texture.from(canvas);
        }
        return this.nebulaTexture;
    }

    /** Inicializa el contexto WebGL conectando PixiJS al canvas existente */
    public async init(): Promise<void> {
        if (this.isReady) return;

        const { width, height } = this.camera.getViewport();
        const initialWidth = width > 0 ? width : window.innerWidth;
        const initialHeight = height > 0 ? height : window.innerHeight;

        await this.app.init({
            canvas: this.canvas,
            width: initialWidth,
            height: initialHeight,
            backgroundColor: 0x0b0e14,
            resolution: window.devicePixelRatio || 1,
            autoDensity: true,
            antialias: true,
            preference: "webgl",
            autoStart: false,
        });

        // Ensamblado en orden estricto de profundidad visual
        this.app.stage.addChild(this.backgroundLayer);
        this.app.stage.addChild(this.nebulaContainer);
        this.app.stage.addChild(this.eventZoneContainer);
        this.app.stage.addChild(this.groundLightsContainer);
        this.app.stage.addChild(this.trailsContainer);
        this.app.stage.addChild(this.foodLayer);
        this.app.stage.addChild(this.foodParticlesContainer);
        this.app.stage.addChild(this.proximityContainer);
        this.app.stage.addChild(this.entityLayer);
        this.app.stage.addChild(this.powerUpsContainer);
        this.app.stage.addChild(this.effectsContainer);
        this.app.stage.addChild(this.floatingTextsContainer);
        this.app.stage.addChild(this.eventHeaderContainer);
        this.app.stage.addChild(this.minimapContainer);

        // Inicializar 3 sprites para las 3 nebulosas cósmicas gigantes
        const nebTex = this.getOrCreateNebulaTexture();
        for (let i = 0; i < 3; i++) {
            const sp = new Sprite(nebTex);
            sp.anchor.set(0.5, 0.5);
            sp.visible = false;
            this.nebulaSprites.push(sp);
            this.nebulaContainer.addChild(sp);
        }

        // Pre-poblar pools para evitar tirones de recolección de basura
        for (let i = 0; i < 32; i++) {
            const view = new PlayerEntityView();
            view.container.visible = false;
            this.entityPool.push(view);
            this.entityLayer.addChild(view.container);
        }

        for (let i = 0; i < 16; i++) {
            const ft = new FloatingTextView();
            ft.container.visible = false;
            this.floatingTextPool.push(ft);
            this.floatingTextsContainer.addChild(ft.container);
        }

        for (let i = 0; i < 24; i++) {
            const dt = new DamageTextView();
            dt.text.visible = false;
            this.damageTextPool.push(dt);
            this.floatingTextsContainer.addChild(dt.text);
        }

        for (let i = 0; i < 12; i++) {
            const pw = new PowerUpView();
            pw.emojiText.visible = false;
            this.powerUpPool.push(pw);
            this.powerUpsContainer.addChild(pw.emojiText);
        }

        this.lastViewportWidth = initialWidth;
        this.lastViewportHeight = initialHeight;
        this.isReady = true;
        void syncTitlesCatalogFromServer();
    }

    /** Redimensiona el buffer de renderizado WebGL */
    public resize(width: number, height: number): void {
        if (!this.isReady) return;
        this.lastViewportWidth = width;
        this.lastViewportHeight = height;
        this.app.renderer.resize(width, height, window.devicePixelRatio || 1);
    }

    /**
     * Dibuja el estado visual del juego completo sobre GPU respetando la firma del Renderer clásico.
     */
    public render(
        players: RenderablePlayer[],
        foods: FoodRenderable[] = [],
        deaths: DeathAnimation[] = [],
        elementalHits: ElementalHitEffect[] = [],
        localSessionId: string | null = null,
        trails: Map<string, TrailPoint[]> = new Map(),
        absorbPulses: Map<string, number> = new Map(),
        nowMs: number = performance.now(),
        foodParticles: FoodParticle[] = [],
        respawnRings: RespawnRing[] = [],
        skin: SkinId | PlayerCosmeticsConfig = "default",
        powerUps: PowerUpRenderable[] = [],
        powerUpPickups: PowerUpPickupEffect[] = [],
        activeEvent: ClientEvent | null = null,
        meteors: MeteorRender[] = [],
        meteorImpacts: MeteorImpactEffect[] = [],
        gravityCaptures: GravityCaptureEffect[] = [],
        floatingTexts: FloatingCombatText[] = [],
        isAttractMode: boolean = false,
        activeEmotes?: Map<string, { emoteId: string; startTime: number; duration: number }>
    ): void {
        if (!this.isReady) return;

        const { width, height } = this.camera.getViewport();

        // Auto-escalado reactivo al viewport de la cámara
        if (width !== this.lastViewportWidth || height !== this.lastViewportHeight) {
            this.resize(width, height);
        }

        // 1. Capa de Fondo (arena, 3 nebulosas cósmicas, círculo de transmutación y motas de parallax)
        this.renderBackground(width, height, isAttractMode, nowMs);

        // 2. Capa de Zonas de Eventos Globales (veneno, pozo gravitatorio, lluvia meteoros, lluvia comida)
        this.renderActiveEvent(activeEvent, width, height, nowMs);

        // 3. Capa de Iluminación Dinámica de Suelo (solo halo para gemas, cero aros oscuros bajo jugadores)
        this.renderGroundLights(players, foods, width, height, nowMs);

        // 4. Estelas elementales temáticas (modo aditivo 'add')
        this.renderTrails(trails, width, height, nowMs);

        // 5. Capa de Comida (gemas rúnicas vivas animadas según food.shape)
        this.renderFoods(foods, width, height, nowMs);

        // 6. Succión magnética de partículas de nutrientes (cola luminosa y núcleo incandescente)
        this.renderFoodParticles(foodParticles, players, width, height, nowMs);

        // 7. Microinteracciones de proximidad elemental (arcos voltaicos hiper-visibles, vapor, zarcillos, chispas)
        this.renderProximityReactions(players, width, height, nowMs);

        // 8. Power-ups y aros de recolección
        this.renderPowerUps(powerUps, powerUpPickups, width, height, nowMs);

        // 9. Capa de Entidades (morfología viva AAA, propulsor alquímico, stretch elástico y cosméticos)
        this.renderPlayers(players, localSessionId, absorbPulses, skin, width, height, nowMs, activeEmotes);

        // 10. Capa de Efectos (anillos de respawn, muertes hiper-explosivas, counters, meteoros, pozos)
        this.renderEffects(respawnRings, deaths, elementalHits, meteors, meteorImpacts, gravityCaptures, width, height, nowMs);

        // 11. Textos de combate flotantes (combos, shutdowns)
        this.renderFloatingTexts(floatingTexts, width, height, nowMs);

        // 12. Minimapa radar táctico (HUD superior siempre visible)
        this.renderMinimap(players, localSessionId, activeEvent, width, height, nowMs);

        // Volcar fotograma al canvas WebGL
        this.app.render();
    }

    /** 2. RESTAURAR EL FONDO CÓSMICO DE CANVAS (NEBULOSAS ULTRA-SUAVES Y POLVO ESTELAR PARALLAX) */
    private renderBackground(
        width: number,
        height: number,
        isAttractMode: boolean,
        nowMs: number
    ): void {
        const g = this.bgGraphics;
        g.clear();

        const zoom = this.camera.getZoom();
        const arenaFloorColor = isAttractMode ? 0x0f172a : 0x0b0e14;
        const voidColor = 0x04060a;

        // Fondo cósmico exterior del Vacío Abisal
        g.rect(0, 0, width, height).fill({ color: voidColor, alpha: 1.0 });

        const mapTopLeft = this.camera.worldToScreen(0, 0);
        const mapBottomRight = this.camera.worldToScreen(this.mapWidth, this.mapHeight);
        const arenaW = mapBottomRight.x - mapTopLeft.x;
        const arenaH = mapBottomRight.y - mapTopLeft.y;
        const arenaCornerRadius = ARENA_CORNER_RADIUS * zoom;

        // 6. BORDES DE LA ARENA CURVOS: Suelo con esquinas redondeadas elegantes (ARENA_CORNER_RADIUS * zoom)
        g.roundRect(mapTopLeft.x, mapTopLeft.y, arenaW, arenaH, arenaCornerRadius).fill({ color: arenaFloorColor, alpha: 1.0 });

        // 4. AURORAS/NEBULOSAS DIFUMINADAS SIN AROS Y EXPANDIDAS FUERA DEL MAPA (> 2400 unidades de mundo)
        const nebWorldRadius = 2600;
        const nebRadius = nebWorldRadius * zoom;
        const nebAlpha = isAttractMode ? 0.12 : 0.13;

        const nebulas = [
            { x: this.mapWidth * 0.2, y: this.mapHeight * 0.2, color: 0x0284c7 },    // Nebulosa celeste cian
            { x: this.mapWidth * 0.8, y: this.mapHeight * 0.2, color: 0x7e22ce },   // Nebulosa violeta mística
            { x: this.mapWidth * 0.5, y: this.mapHeight * 0.8, color: 0x059669 },  // Nebulosa esmeralda
        ];

        for (let i = 0; i < nebulas.length; i++) {
            const neb = nebulas[i];
            const sprite = this.nebulaSprites[i];
            if (!sprite) continue;

            const scr = this.camera.worldToScreen(neb.x, neb.y);

            // Culling de nebulosa en pantalla
            if (
                scr.x + nebRadius < 0 || scr.x - nebRadius > width ||
                scr.y + nebRadius < 0 || scr.y - nebRadius > height
            ) {
                sprite.visible = false;
                continue;
            }

            sprite.position.set(scr.x, scr.y);
            sprite.width = nebRadius * 2;
            sprite.height = nebRadius * 2;
            sprite.tint = neb.color;
            sprite.alpha = nebAlpha;
            sprite.visible = true;
        }

        // 5. CÍRCULO ALQUÍMICO / ESTRELLA CENTRAL MÁS GRANDE (850px en escala de mundo)
        const centerWorldX = this.mapWidth / 2;
        const centerWorldY = this.mapHeight / 2;
        const centerScreen = this.camera.worldToScreen(centerWorldX, centerWorldY);
        const circleRadius = 850 * zoom;

        if (
            centerScreen.x + circleRadius >= 0 && centerScreen.x - circleRadius <= width &&
            centerScreen.y + circleRadius >= 0 && centerScreen.y - circleRadius <= height
        ) {
            const cAlpha1 = isAttractMode ? 0.08 : 0.095;
            const cAlpha2 = isAttractMode ? 0.07 : 0.085;

            // Dos anillos concéntricos principales de 850px y 790px
            g.circle(centerScreen.x, centerScreen.y, circleRadius).stroke({
                color: 0x38bdf8,
                width: Math.max(1, 2.0 * zoom),
                alpha: cAlpha1,
            });
            g.circle(centerScreen.x, centerScreen.y, 790 * zoom).stroke({
                color: 0xa855f7,
                width: Math.max(1, 1.8 * zoom),
                alpha: cAlpha2,
            });

            // Símbolos y marcas radiales tenues entre ambos anillos exteriores
            const markCount = 24;
            for (let m = 0; m < markCount; m++) {
                const mAng = (m / markCount) * Math.PI * 2;
                const cosM = Math.cos(mAng);
                const sinM = Math.sin(mAng);
                const innerR = 790 * zoom;
                const outerR = (m % 2 === 0 ? 840 : 815) * zoom;
                g.moveTo(centerScreen.x + cosM * innerR, centerScreen.y + sinM * innerR)
                    .lineTo(centerScreen.x + cosM * outerR, centerScreen.y + sinM * outerR)
                    .stroke({ color: 0x38bdf8, width: Math.max(1, 1.2 * zoom), alpha: cAlpha1 * 0.7 });
            }

            // Hexagrama alquímico rotando suavemente (2 triángulos equiláteros opuestos de 760px)
            const rotHex = nowMs * 0.00015;
            const triRadius = 760 * zoom;
            for (let t = 0; t < 2; t++) {
                const angleOffset = rotHex + t * Math.PI;
                const triPts: number[] = [];
                for (let k = 0; k < 3; k++) {
                    const ang = angleOffset + (k * 2 * Math.PI) / 3;
                    triPts.push(centerScreen.x + Math.cos(ang) * triRadius, centerScreen.y + Math.sin(ang) * triRadius);
                }
                g.poly(triPts, true).stroke({ color: 0x38bdf8, width: Math.max(1, 1.6 * zoom), alpha: cAlpha1 });
            }

            // Cuadrado inscrito en contra-rotación
            const rotSq = -nowMs * 0.0001;
            const sqRadius = 520 * zoom;
            const sqPts: number[] = [];
            for (let k = 0; k < 4; k++) {
                const ang = rotSq + (k * Math.PI) / 2 + Math.PI / 4;
                sqPts.push(centerScreen.x + Math.cos(ang) * sqRadius, centerScreen.y + Math.sin(ang) * sqRadius);
            }
            g.poly(sqPts, true).stroke({ color: 0xa855f7, width: Math.max(1, 1.5 * zoom), alpha: cAlpha2 });

            // Círculos concéntricos interiores
            g.circle(centerScreen.x, centerScreen.y, 280 * zoom).stroke({
                color: 0x38bdf8,
                width: Math.max(1, 1.5 * zoom),
                alpha: cAlpha1,
            });
            g.circle(centerScreen.x, centerScreen.y, 140 * zoom).stroke({
                color: 0xa855f7,
                width: Math.max(1, 1.5 * zoom),
                alpha: cAlpha2,
            });
        }

        // 2.3. 45 MOTAS DE ENERGÍA AMBIENTAL CON PARALLAX 2.5D
        const camCenter = this.camera.screenToWorld(width / 2, height / 2);
        const motesCount = 45;
        const moteColors = [0x38bdf8, 0xa78bfa];

        for (let m = 0; m < motesCount; m++) {
            const layer = m % 2 === 0 ? 0.15 : 0.35;
            const baseX = ((m * 179 + 37) % 3000);
            const baseY = ((m * 223 + 71) % 3000);

            const screenX = ((baseX - camCenter.x * layer + width * 10) % width);
            const screenY = ((baseY - camCenter.y * layer + height * 10) % height);

            const pulse = Math.sin(nowMs * 0.002 + m * 1.7);
            const alpha = 0.10 + (0.5 + 0.5 * pulse) * 0.18;
            const mRadius = Math.max(1.2, 1.2 + (m % 3) * 0.8);

            g.circle(screenX, screenY, mRadius).fill({ color: moteColors[m % moteColors.length], alpha });
        }

        // 2.4. Cuadrícula de coordenadas cada 100 unidades dentro de la arena
        const topLeftWorld = this.camera.screenToWorld(0, 0);
        const bottomRightWorld = this.camera.screenToWorld(width, height);

        const boundsLeft = Math.max(0, Math.min(topLeftWorld.x, bottomRightWorld.x));
        const boundsRight = Math.min(this.mapWidth, Math.max(topLeftWorld.x, bottomRightWorld.x));
        const boundsTop = Math.max(0, Math.min(topLeftWorld.y, bottomRightWorld.y));
        const boundsBottom = Math.min(this.mapHeight, Math.max(topLeftWorld.y, bottomRightWorld.y));

        const gridSize = 100;
        const startX = Math.floor(boundsLeft / gridSize) * gridSize;
        const endX = Math.ceil(boundsRight / gridSize) * gridSize;
        const startY = Math.floor(boundsTop / gridSize) * gridSize;
        const endY = Math.ceil(boundsBottom / gridSize) * gridSize;

        for (let wx = startX; wx <= endX; wx += gridSize) {
            const p1 = this.camera.worldToScreen(wx, boundsTop);
            const p2 = this.camera.worldToScreen(wx, boundsBottom);
            g.moveTo(p1.x, p1.y).lineTo(p2.x, p2.y);
        }

        for (let wy = startY; wy <= endY; wy += gridSize) {
            const p1 = this.camera.worldToScreen(boundsLeft, wy);
            const p2 = this.camera.worldToScreen(boundsRight, wy);
            g.moveTo(p1.x, p1.y).lineTo(p2.x, p2.y);
        }
        g.stroke({ color: 0xffffff, alpha: 0.02, width: 1 });

        // 6. BORDES DE LA ARENA CURVOS: Valla perimetral de neón cian con esquinas redondeadas (180 * zoom)
        g.roundRect(mapTopLeft.x, mapTopLeft.y, arenaW, arenaH, arenaCornerRadius).stroke({
            color: 0x38bdf8,
            alpha: 0.35,
            width: Math.max(2, 2.5 * zoom),
        });
    }

    /** 5. RESTAURAR ZONAS DE EVENTOS GLOBALES (renderActiveEvent) */
    private renderActiveEvent(
        activeEvent: ClientEvent | null,
        width: number,
        height: number,
        nowMs: number
    ): void {
        const g = this.eventZoneGraphics;
        g.clear();

        if (!activeEvent) {
            this.eventHeaderText.visible = false;
            return;
        }

        const center = this.camera.worldToScreen(activeEvent.x, activeEvent.y);
        const zoom = this.camera.getZoom();
        const screenRadius = activeEvent.radius * zoom;

        // Culling estricto de pantalla
        if (
            center.x + screenRadius < -120 ||
            center.x - screenRadius > width + 120 ||
            center.y + screenRadius < -120 ||
            center.y - screenRadius > height + 120
        ) {
            this.eventHeaderText.visible = false;
            return;
        }

        const eventColor = (activeEvent.color && activeEvent.color.trim().length > 0)
            ? parseColorToHex(activeEvent.color, EVENT_HEX_COLORS[activeEvent.type] ?? 0x38bdf8)
            : (EVENT_HEX_COLORS[activeEvent.type] ?? 0x38bdf8);
        const pulse = 0.5 + 0.5 * Math.sin(nowMs * 0.004);

        // Anillo de advertencia perimetral pulsante
        g.circle(center.x, center.y, screenRadius + 4 * zoom).stroke({
            color: eventColor,
            width: Math.max(1.5, 4 * zoom),
            alpha: 0.35 + pulse * 0.3,
        });
        g.circle(center.x, center.y, screenRadius).stroke({
            color: eventColor,
            width: Math.max(2, 3.5 * zoom),
            alpha: 0.65 + pulse * 0.35,
        });
        g.circle(center.x, center.y, Math.max(1, screenRadius - 8 * zoom)).stroke({
            color: 0xffffff,
            width: Math.max(1, 1.5 * zoom),
            alpha: 0.25 + pulse * 0.25,
        });

        // Campo interior teñido según tipo de evento
        switch (activeEvent.type) {
            case "poison-zone": {
                const toxicAlpha = Math.max(0.06, 0.14 + 0.08 * Math.sin(nowMs / 200));
                g.circle(center.x, center.y, screenRadius).fill({ color: eventColor, alpha: toxicAlpha });

                // 25 burbujas de niebla tóxica flotando
                const particleCount = 25;
                for (let i = 0; i < particleCount; i++) {
                    const baseAngle = (i * 137.5 * Math.PI) / 180;
                    const baseDist = Math.sqrt((i + 1) / particleCount) * (screenRadius * 0.85);
                    const driftAngle = baseAngle + Math.sin(nowMs * 0.001 + i) * 0.35;
                    const driftDist = baseDist + Math.cos(nowMs * 0.0015 + i * 2) * 12 * zoom;

                    const px = center.x + Math.cos(driftAngle) * driftDist;
                    const py = center.y + Math.sin(driftAngle) * driftDist;
                    const pAlpha = Math.max(0, 0.25 + 0.25 * Math.sin(nowMs * 0.003 + i));
                    const pSize = Math.max(1.5, (3 + (i % 3)) * zoom);

                    g.circle(px, py, pSize).fill({ color: 0x4ade80, alpha: pAlpha * 0.8 });
                    g.circle(px - pSize * 0.25, py - pSize * 0.25, pSize * 0.35).fill({ color: 0xdcfce7, alpha: pAlpha });
                }
                break;
            }
            case "gravity-well": {
                g.circle(center.x, center.y, screenRadius).fill({ color: 0x581c87, alpha: 0.12 });

                // 4 anillos concéntricos
                const ringCount = 4;
                for (let k = 1; k <= ringCount; k++) {
                    const r = (screenRadius * k) / ringCount;
                    const alpha = 0.15 + (1 - k / ringCount) * 0.35;
                    g.circle(center.x, center.y, r).stroke({ color: 0xa855f7, width: Math.max(1, 2 * zoom), alpha });
                }

                // 16 líneas radiales atrayentes en rotación
                const lineCount = 16;
                const rotOffset = (nowMs * 0.0006) % (Math.PI * 2);
                const coreR = Math.max(10, 24 * zoom);

                for (let i = 0; i < lineCount; i++) {
                    const angle = (i / lineCount) * Math.PI * 2 + rotOffset;
                    const x1 = center.x + Math.cos(angle) * screenRadius;
                    const y1 = center.y + Math.sin(angle) * screenRadius;
                    const x2 = center.x + Math.cos(angle) * coreR;
                    const y2 = center.y + Math.sin(angle) * coreR;
                    const linePulse = Math.max(0.08, 0.2 + 0.25 * Math.sin(nowMs * 0.005 + i));

                    g.moveTo(x1, y1).lineTo(x2, y2).stroke({ color: 0xc084fc, width: Math.max(1, 1.5 * zoom), alpha: linePulse });
                }

                // Núcleo de singularidad
                g.circle(center.x, center.y, coreR * 1.3).fill({ color: 0xc084fc, alpha: 0.4 });
                g.circle(center.x, center.y, coreR).fill({ color: 0x7e22ce, alpha: 0.95 });
                g.circle(center.x, center.y, coreR).stroke({ color: 0xf3e8ff, width: Math.max(1.5, 2.5 * zoom) });
                break;
            }
            case "meteor-shower": {
                g.circle(center.x, center.y, screenRadius).fill({ color: 0x7f1d1d, alpha: 0.18 });

                const crossSize = Math.max(12, 28 * zoom);
                g.moveTo(center.x - crossSize, center.y).lineTo(center.x + crossSize, center.y).stroke({
                    color: 0xef4444,
                    width: Math.max(1.5, 2.5 * zoom),
                    alpha: 0.85,
                });
                g.moveTo(center.x, center.y - crossSize).lineTo(center.x, center.y + crossSize).stroke({
                    color: 0xef4444,
                    width: Math.max(1.5, 2.5 * zoom),
                    alpha: 0.85,
                });
                break;
            }
            case "food-rain": {
                g.circle(center.x, center.y, screenRadius).fill({ color: 0xf59e0b, alpha: 0.12 });

                const moteCount = 20;
                for (let i = 0; i < moteCount; i++) {
                    const ang = (i / moteCount) * Math.PI * 2;
                    const d = ((nowMs * 0.04 + i * 19) % screenRadius);
                    const mx = center.x + Math.cos(ang) * d;
                    const my = center.y + Math.sin(ang) * d - (nowMs * 0.03 + i * 10) % 30;
                    const mAlpha = 0.4 + 0.4 * Math.sin(nowMs * 0.005 + i);

                    g.circle(mx, my, Math.max(1.5, 2.8 * zoom)).fill({ color: 0xfef08a, alpha: mAlpha });
                }
                break;
            }
            default:
                g.circle(center.x, center.y, screenRadius).fill({ color: eventColor, alpha: 0.10 });
                break;
        }

        // Etiqueta grande legible centrada en la parte superior con emoji y nombre
        const label = `${activeEvent.emoji} ${activeEvent.name.toUpperCase()}`;
        if (this.eventHeaderText.text !== label) {
            this.eventHeaderText.text = label;
        }
        this.eventHeaderText.style.fill = eventColor;
        const textY = Math.max(30, center.y - screenRadius - 22 * zoom);
        this.eventHeaderText.position.set(center.x, textY);
        this.eventHeaderText.scale.set(Math.max(0.75, Math.min(1.3, zoom)));
        this.eventHeaderText.visible = true;
    }

    /** 1. ELIMINACIÓN DE LOS AROS OSCUROS ESCALONADOS ALREDEDOR DE LOS JUGADORES */
    private renderGroundLights(
        _players: RenderablePlayer[],
        foods: FoodRenderable[],
        width: number,
        height: number,
        _nowMs: number
    ): void {
        const g = this.groundLightGraphics;
        g.clear();

        const zoom = this.camera.getZoom();

        // NOTA: Se eliminaron completamente los 5 círculos discretos concéntricos bajo jugadores para dejar su silueta limpia.

        // Halo de luz aditiva suave de 2.5x bajo cada gema de comida en la arena oscura
        const foodScaledR = FOOD_RADIUS * zoom;
        const foodHaloR = foodScaledR * 2.5;

        for (let j = 0; j < foods.length; j++) {
            const food = foods[j];
            const screen = this.camera.worldToScreen(food.x, food.y);

            if (
                screen.x + foodHaloR < 0 || screen.x - foodHaloR > width ||
                screen.y + foodHaloR < 0 || screen.y - foodHaloR > height
            ) {
                continue;
            }

            const col = FOOD_HEX_COLORS[food.shape % FOOD_HEX_COLORS.length] ?? FOOD_HEX_COLORS[0];
            g.circle(screen.x, screen.y, foodHaloR).fill({ color: col, alpha: 0.14 });
            g.circle(screen.x, screen.y, foodScaledR * 1.5).fill({ color: col, alpha: 0.24 });
        }
    }

    /** ESTELAS ELEMENTALES TEMÁTICAS con modo de fusión aditivo ('add') */
    private renderTrails(
        trails: Map<string, TrailPoint[]>,
        width: number,
        height: number,
        nowMs: number
    ): void {
        const g = this.trailsGraphics;
        g.clear();

        const zoom = this.camera.getZoom();

        for (const [, trail] of trails) {
            if (trail.length < 2) continue;

            for (let i = 1; i < trail.length; i++) {
                const p0 = trail[i - 1];
                const p1 = trail[i];
                const age = (nowMs - p1.time) / 200; // 0 a 1
                if (age >= 1 || age < 0) continue;

                const screen0 = this.camera.worldToScreen(p0.x, p0.y);
                const screen1 = this.camera.worldToScreen(p1.x, p1.y);

                // Culling de pantalla
                if (
                    (screen1.x < -50 && screen0.x < -50) ||
                    (screen1.x > width + 50 && screen0.x > width + 50) ||
                    (screen1.y < -50 && screen0.y < -50) ||
                    (screen1.y > height + 50 && screen0.y > height + 50)
                ) {
                    continue;
                }

                const alpha = (1 - age) * 0.65;
                const r = Math.max(1.5, p1.radius * (1 - age) * 0.38 * zoom);
                const seed = p1.seed;

                switch (p1.element) {
                    case "water": {
                        const offsetX = (seed - 0.5) * r * 0.8;
                        const bx = screen1.x + offsetX;
                        const by = screen1.y;

                        g.circle(bx, by, r).fill({ color: 0x38bdf8, alpha: alpha * 0.6 });
                        g.circle(bx, by, r).stroke({ color: 0xbae6fd, width: Math.max(1, 1.2 * zoom), alpha: alpha * 0.85 });
                        break;
                    }
                    case "fire": {
                        const fireColor = age > 0.5
                            ? 0x78716c
                            : (i % 2 === 0 ? 0xf97316 : 0xef4444);
                        const liftY = screen1.y - age * 14 * zoom;
                        const emberR = Math.max(1.2, r * (age > 0.5 ? 0.9 : 0.6));

                        g.circle(screen1.x + (seed - 0.5) * 6 * zoom, liftY, emberR).fill({ color: fireColor, alpha });
                        g.circle(screen1.x + (seed - 0.5) * 6 * zoom, liftY, emberR * 0.5).fill({ color: 0xfef08a, alpha: alpha * 0.8 });
                        break;
                    }
                    case "earth": {
                        const rockColor = i % 2 === 0 ? 0xb45309 : 0x78350f;
                        const ang = seed * Math.PI * 2;
                        const cos = Math.cos(ang);
                        const sin = Math.sin(ang);
                        const pX = [0, r * 0.8, 0, -r * 0.8];
                        const pY = [-r, 0, r, 0];
                        const pts: number[] = [];
                        for (let k = 0; k < 4; k++) {
                            pts.push(screen1.x + pX[k] * cos - pY[k] * sin, screen1.y + pX[k] * sin + pY[k] * cos);
                        }
                        g.poly(pts, true).fill({ color: rockColor, alpha: alpha * 0.75 });
                        break;
                    }
                    case "plant": {
                        if (i % 3 === 0) {
                            g.circle(screen1.x, screen1.y, Math.max(1.5, r * 0.45)).fill({ color: 0xfde047, alpha: alpha * 0.9 });
                        } else {
                            g.circle(screen1.x, screen1.y, r).fill({ color: 0x22c55e, alpha });
                            g.circle(screen1.x, screen1.y, r * 0.5).fill({ color: 0x86efac, alpha: alpha * 0.7 });
                        }
                        break;
                    }
                    case "electric": {
                        const dx = screen1.x - screen0.x;
                        const dy = screen1.y - screen0.y;
                        const dist = Math.hypot(dx, dy);
                        const perpX = dist > 0.001 ? (-dy / dist) : 0;
                        const perpY = dist > 0.001 ? (dx / dist) : 0;
                        const midX = (screen0.x + screen1.x) * 0.5 + perpX * (seed - 0.5) * 14 * zoom;
                        const midY = (screen0.y + screen1.y) * 0.5 + perpY * (seed - 0.5) * 14 * zoom;

                        g.moveTo(screen0.x, screen0.y)
                            .lineTo(midX, midY)
                            .lineTo(screen1.x, screen1.y)
                            .stroke({
                                color: i % 2 === 0 ? 0xfacc15 : 0xffffff,
                                width: Math.max(1, 1.8 * zoom),
                                alpha,
                            });
                        break;
                    }
                    default: {
                        const col = ELEMENT_HEX_COLORS[p1.element] ?? 0x888888;
                        g.moveTo(screen0.x, screen0.y)
                            .lineTo(screen1.x, screen1.y)
                            .stroke({ color: col, width: r, alpha: alpha * 0.5 });
                        break;
                    }
                }
            }
        }
    }

    /** REDISEÑO DE COMIDA EN GEMAS RÚNICAS RESPLANDECIENTES */
    private renderFoods(foods: FoodRenderable[], width: number, height: number, nowMs: number): void {
        const g = this.foodGraphics;
        g.clear();

        const foodZoom = this.camera.getZoom();
        const baseRadius = FOOD_RADIUS * foodZoom;
        const cullingMargin = baseRadius * 3;
        const count = foods.length;

        for (let i = 0; i < count; i++) {
            const food = foods[i];
            const screen = this.camera.worldToScreen(food.x, food.y);

            if (
                screen.x + cullingMargin < 0 || screen.x - cullingMargin > width ||
                screen.y + cullingMargin < 0 || screen.y - cullingMargin > height
            ) {
                continue;
            }

            const shape = food.shape % 5;
            const r = Math.max(2, baseRadius);

            switch (shape) {
                // 0 (Fuego): Brasa de flama ascendente con núcleo amarillo brillante y oscilación sinusoidal
                case 0: {
                    const bob = Math.sin(nowMs * 0.006 + food.x * 0.05) * (r * 0.25);
                    const fx = screen.x;
                    const fy = screen.y + bob;

                    const flamePts = [
                        fx - r * 0.7, fy + r * 0.6,
                        fx, fy - r * 1.3,
                        fx + r * 0.7, fy + r * 0.6,
                        fx, fy + r * 0.2,
                    ];
                    g.poly(flamePts, true).fill({ color: 0xef4444, alpha: 0.95 });
                    g.circle(fx, fy - r * 0.1, r * 0.45).fill({ color: 0xfef08a, alpha: 0.95 });
                    break;
                }

                // 1 (Agua): Gota de cristal líquido con brillo especular blanco puro
                case 1: {
                    const gx = screen.x;
                    const gy = screen.y;
                    const dropPts = [
                        gx, gy - r * 1.25,
                        gx + r * 0.8, gy + r * 0.3,
                        gx, gy + r * 0.9,
                        gx - r * 0.8, gy + r * 0.3,
                    ];
                    g.poly(dropPts, true)
                        .fill({ color: 0x0ea5e9, alpha: 0.95 })
                        .stroke({ color: 0x38bdf8, width: 1 });
                    g.circle(gx - r * 0.25, gy - r * 0.15, Math.max(1, r * 0.3)).fill({ color: 0xffffff, alpha: 0.85 });
                    break;
                }

                // 2 (Planta): Hoja esmeralda estilizada con nervadura central luminosa
                case 2: {
                    const hx = screen.x;
                    const hy = screen.y;
                    const leafPts = [
                        hx, hy - r * 1.2,
                        hx + r * 0.75, hy,
                        hx, hy + r * 1.2,
                        hx - r * 0.75, hy,
                    ];
                    g.poly(leafPts, true)
                        .fill({ color: 0x22c55e, alpha: 0.95 })
                        .stroke({ color: 0x15803d, width: 1 });
                    // Nervadura central luminosa
                    g.moveTo(hx, hy - r * 0.9)
                        .lineTo(hx, hy + r * 0.9)
                        .stroke({ color: 0x86efac, width: Math.max(1, 1.2 * foodZoom), alpha: 0.9 });
                    break;
                }

                // 3 (Eléctrico): Estrella voltaica de 4 puntas en rotación continua (spin)
                case 3: {
                    const rot = nowMs * 0.003;
                    const pts: number[] = [];
                    for (let s = 0; s < 8; s++) {
                        const ang = rot + (s * Math.PI) / 4;
                        const dist = s % 2 === 0 ? r * 1.25 : r * 0.42;
                        pts.push(screen.x + Math.cos(ang) * dist, screen.y + Math.sin(ang) * dist);
                    }
                    g.poly(pts, true).fill({ color: 0xfacc15, alpha: 0.95 });
                    g.circle(screen.x, screen.y, r * 0.35).fill({ color: 0xffffff, alpha: 0.95 });
                    break;
                }

                // 4 (Tierra): Gema romboidal/hexagonal facetada con aristas doradas
                case 4:
                default: {
                    const zx = screen.x;
                    const zy = screen.y;
                    const gemPts = [
                        zx, zy - r * 1.15,
                        zx + r * 0.95, zy - r * 0.35,
                        zx + r * 0.75, zy + r * 0.95,
                        zx - r * 0.75, zy + r * 0.95,
                        zx - r * 0.95, zy - r * 0.35,
                    ];
                    g.poly(gemPts, true)
                        .fill({ color: 0xd97706, alpha: 0.95 })
                        .stroke({ color: 0xfde68a, width: Math.max(1, 1.2 * foodZoom) });
                    // Faceta dorada interior
                    g.moveTo(zx, zy - r * 1.15).lineTo(zx, zy + r * 0.4).stroke({ color: 0xfde68a, width: 1, alpha: 0.7 });
                    g.moveTo(zx - r * 0.95, zy - r * 0.35).lineTo(zx, zy + r * 0.4).stroke({ color: 0xfde68a, width: 1, alpha: 0.7 });
                    g.moveTo(zx + r * 0.95, zy - r * 0.35).lineTo(zx, zy + r * 0.4).stroke({ color: 0xfde68a, width: 1, alpha: 0.7 });
                    break;
                }
            }
        }
    }

    /** SUCCIÓN MAGNÉTICA DE NUTRIENTES REPOTENCIADA */
    private renderFoodParticles(
        foodParticles: FoodParticle[],
        players: RenderablePlayer[],
        width: number,
        height: number,
        nowMs: number
    ): void {
        const g = this.foodParticlesGraphics;
        g.clear();

        const zoom = this.camera.getZoom();
        const count = foodParticles.length;

        for (let i = 0; i < count; i++) {
            const p = foodParticles[i];
            const progress = Math.min(1, (nowMs - p.startTime) / p.duration);
            if (progress >= 1 || progress < 0) continue;

            let px = p.x + p.vx * progress * 40;
            let py = p.y + p.vy * progress * 40;
            let dirX = p.vx;
            let dirY = p.vy;

            if (p.targetSessionId) {
                const targetPlayer = players.find((pl) => pl.sessionId === p.targetSessionId);
                if (targetPlayer && targetPlayer.alive) {
                    const pull = progress * progress;
                    dirX = targetPlayer.renderX - px;
                    dirY = targetPlayer.renderY - py;
                    px = px * (1 - pull) + targetPlayer.renderX * pull;
                    py = py * (1 - pull) + targetPlayer.renderY * pull;
                }
            }

            const screen = this.camera.worldToScreen(px, py);

            if (screen.x < -30 || screen.x > width + 30 || screen.y < -30 || screen.y > height + 30) {
                continue;
            }

            const size = Math.max(2, (5.5 + (1 - progress) * 2.0) * zoom);
            const alpha = Math.max(0, 1 - progress * 0.75);

            // Cola de aceleración luminosa hacia el jugador
            const dist = Math.hypot(dirX, dirY);
            if (dist > 0.001) {
                const normX = dirX / dist;
                const normY = dirY / dist;
                const tailLen = (14 * progress + 6) * zoom;
                g.moveTo(screen.x - normX * tailLen, screen.y - normY * tailLen)
                    .lineTo(screen.x, screen.y)
                    .stroke({ color: p.color, width: Math.max(1.5, size * 0.7), alpha: alpha * 0.75 });
            }

            // Halo difuso exterior aditivo
            g.circle(screen.x, screen.y, size * 2.4).fill({ color: p.color, alpha: alpha * 0.4 });

            // Cuerpo colorido
            g.circle(screen.x, screen.y, size).fill({ color: p.color, alpha });

            // Núcleo blanco brillante
            g.circle(screen.x, screen.y, size * 0.42).fill({ color: 0xffffff, alpha: alpha * 0.95 });
        }
    }

    /** 6. MICROINTERACCIONES DE PROXIMIDAD HIPER-VISIBLES (<= 85px) */
    private renderProximityReactions(
        players: RenderablePlayer[],
        width: number,
        height: number,
        nowMs: number
    ): void {
        const g = this.proximityGraphics;
        g.clear();

        const zoom = this.camera.getZoom();
        const canSpawn = nowMs - this.lastProximitySpawnAt >= 32;
        let spawnedThisFrame = false;

        // Detección de pares de jugadores vivos con distancia entre bordes <= 85px
        for (let i = 0; i < players.length; i++) {
            const pA = players[i];
            if (!pA.alive) continue;
            const rA = Math.sqrt(pA.mass) * 2;

            for (let j = i + 1; j < players.length; j++) {
                const pB = players[j];
                if (!pB.alive) continue;
                if (pA.element === pB.element) continue;
                const rB = Math.sqrt(pB.mass) * 2;

                const dx = pB.renderX - pA.renderX;
                const dy = pB.renderY - pA.renderY;
                const centerDist = Math.hypot(dx, dy);
                const edgeGap = centerDist - (rA + rB);

                if (edgeGap > 85 || centerDist < 1) continue;

                const nx = dx / centerDist;
                const ny = dy / centerDist;
                const midWorldX = pA.renderX + nx * (rA + edgeGap * 0.5);
                const midWorldY = pA.renderY + ny * (rA + edgeGap * 0.5);
                const screenMid = this.camera.worldToScreen(midWorldX, midWorldY);

                if (
                    screenMid.x < -80 || screenMid.x > width + 80 ||
                    screenMid.y < -80 || screenMid.y > height + 80
                ) {
                    continue;
                }

                const pair = `${pA.element}+${pB.element}`;

                // 6.1. Electricidad + Agua: Relámpagos bifurcados gruesos (3-4px) en zigzag blanco y cian (#38bdf8)
                if (pair === "electric+water" || pair === "water+electric") {
                    const edgeA = this.camera.worldToScreen(pA.renderX + nx * rA, pA.renderY + ny * rA);
                    const edgeB = this.camera.worldToScreen(pB.renderX - nx * rB, pB.renderY - ny * rB);

                    const perpX = -ny * Math.sin(nowMs * 0.09 + i) * 16 * zoom;
                    const perpY = nx * Math.sin(nowMs * 0.09 + i) * 16 * zoom;

                    // Arco primario bifurcado grueso
                    g.moveTo(edgeA.x, edgeA.y)
                        .lineTo(screenMid.x + perpX, screenMid.y + perpY)
                        .lineTo(edgeB.x, edgeB.y)
                        .stroke({ color: 0xffffff, width: Math.max(2.5, 3.8 * zoom), alpha: 0.98 });

                    // Rama secundaria cian desprendiéndose
                    g.moveTo(screenMid.x + perpX, screenMid.y + perpY)
                        .lineTo(screenMid.x + (-ny * 0.6 + nx * 0.4) * 22 * zoom, screenMid.y + (nx * 0.6 + ny * 0.4) * 22 * zoom)
                        .stroke({ color: 0x38bdf8, width: Math.max(1.8, 2.5 * zoom), alpha: 0.92 });

                    // Flash de arco estroboscópico en los extremos y centro
                    const flashPulse = 0.7 + 0.3 * Math.sin(nowMs * 0.04);
                    g.circle(edgeA.x, edgeA.y, Math.max(4, 7 * zoom)).fill({ color: 0xffffff, alpha: flashPulse });
                    g.circle(edgeB.x, edgeB.y, Math.max(4, 7 * zoom)).fill({ color: 0x38bdf8, alpha: flashPulse });
                    g.circle(screenMid.x + perpX, screenMid.y + perpY, Math.max(5, 9 * zoom)).fill({ color: 0xffffff, alpha: flashPulse });
                }

                // 6.2. Fuego + Agua: Grandes nubes de vapor térmico blanco/grisáceo (#f1f5f9) de 15-20px
                if (canSpawn && (pair === "fire+water" || pair === "water+fire")) {
                    this.spawnProximityParticle(midWorldX, midWorldY, "steam", nowMs);
                    spawnedThisFrame = true;
                }

                // 6.3. Tierra + Planta: Enredaderas y zarcillos neón (#22c55e y #4ade80) con esporas doradas
                if (pair === "earth+plant" || pair === "plant+earth") {
                    const edgeA = this.camera.worldToScreen(pA.renderX + nx * rA, pA.renderY + ny * rA);
                    const edgeB = this.camera.worldToScreen(pB.renderX - nx * rB, pB.renderY - ny * rB);
                    const vineWave = Math.sin(nowMs * 0.006 + i) * 20 * zoom;

                    g.moveTo(edgeA.x, edgeA.y)
                        .quadraticCurveTo(screenMid.x + (-ny) * vineWave, screenMid.y + nx * vineWave, edgeB.x, edgeB.y)
                        .stroke({ color: 0x22c55e, width: Math.max(2.5, 3.8 * zoom), alpha: 0.95 });

                    g.moveTo(edgeA.x, edgeA.y)
                        .quadraticCurveTo(screenMid.x - (-ny) * (vineWave * 0.8), screenMid.y - nx * (vineWave * 0.8), edgeB.x, edgeB.y)
                        .stroke({ color: 0x4ade80, width: Math.max(1.8, 2.8 * zoom), alpha: 0.92 });

                    if (canSpawn) {
                        this.spawnProximityParticle(midWorldX, midWorldY, "root-bloom", nowMs);
                        spawnedThisFrame = true;
                    }
                }

                // 6.4. Fuego + Planta: Ráfagas de chispas ardientes (#f97316) con destellos de combustión
                if (pair === "fire+plant" || pair === "plant+fire") {
                    // Flash nuclear de combustión en el centro
                    const cPulse = 0.6 + 0.4 * Math.sin(nowMs * 0.02);
                    g.circle(screenMid.x, screenMid.y, Math.max(4, 8 * zoom)).fill({ color: 0xfef08a, alpha: cPulse });
                    g.circle(screenMid.x, screenMid.y, Math.max(7, 14 * zoom)).fill({ color: 0xef4444, alpha: cPulse * 0.65 });

                    if (canSpawn) {
                        this.spawnProximityParticle(midWorldX, midWorldY, "ember-spark", nowMs);
                        spawnedThisFrame = true;
                    }
                }
            }
        }

        if (spawnedThisFrame) {
            this.lastProximitySpawnAt = nowMs;
        }

        // Renderizado y ciclo de vida de partículas de proximidad del pool
        for (let k = 0; k < this.proximityParticlesPool.length; k++) {
            const pt = this.proximityParticlesPool[k];
            if (!pt.inUse) continue;

            const elapsed = nowMs - pt.birthTime;
            if (elapsed >= pt.duration) {
                pt.inUse = false;
                continue;
            }

            const t = elapsed / pt.duration;
            const wx = pt.x + pt.vx * (elapsed * 0.001);
            const wy = pt.y + pt.vy * (elapsed * 0.001);
            const scr = this.camera.worldToScreen(wx, wy);

            if (scr.x < -40 || scr.x > width + 40 || scr.y < -40 || scr.y > height + 40) {
                continue;
            }

            switch (pt.kind) {
                case "steam": {
                    // 7. Vapor térmico sutil como humo tenue gris-azulado (0x94a3b8 / 0x64748b, alpha: 0.15 - 0.22)
                    const smokeAlpha = (1 - t) * 0.19;
                    const sR = pt.radius * (1 + t * 2.2) * zoom;
                    const driftX = Math.sin(t * 4 + pt.birthTime * 0.01) * 12 * zoom;
                    const driftY = -t * 18 * zoom;

                    // Bocanada principal difusa y tenue
                    g.circle(scr.x + driftX, scr.y + driftY, sR).fill({ color: 0x94a3b8, alpha: smokeAlpha });
                    // Voluta secundaria flotante más oscura para dar profundidad de humo
                    g.circle(scr.x + driftX * 0.6, scr.y + driftY * 0.7, sR * 0.75).fill({ color: 0x64748b, alpha: smokeAlpha * 0.8 });
                    // Estela difusa tenue
                    g.circle(scr.x + driftX * 1.3, scr.y + driftY * 1.2, sR * 0.55).fill({ color: 0x94a3b8, alpha: smokeAlpha * 0.6 });
                    break;
                }
                case "root-bloom": {
                    // Esporas doradas y brotes esmeralda
                    const rR = pt.radius * Math.sin(t * Math.PI) * zoom;
                    g.circle(scr.x, scr.y, rR).fill({ color: 0x4ade80, alpha: (1 - t) * 0.9 });
                    g.circle(scr.x, scr.y, rR * 0.5).fill({ color: 0xfde047, alpha: 1 - t });
                    break;
                }
                case "ember-spark": {
                    // Chispas ardientes (#f97316)
                    const col = t < 0.35 ? 0xfef08a : 0xf97316;
                    g.circle(scr.x, scr.y, Math.max(2, pt.radius * (1 - t) * zoom)).fill({
                        color: col,
                        alpha: 1 - t,
                    });
                    break;
                }
            }
        }
    }

    /** Dispara una partícula de proximidad reutilizando un slot libre del pool circular */
    private spawnProximityParticle(
        x: number,
        y: number,
        kind: "steam" | "root-bloom" | "ember-spark",
        nowMs: number
    ): void {
        const pt = this.proximityParticlesPool[this.nextProximityIdx];
        this.nextProximityIdx = (this.nextProximityIdx + 1) % this.proximityParticlesPool.length;

        const angle = Math.random() * Math.PI * 2;
        const speed = kind === "steam" ? 22 + Math.random() * 25 : 20 + Math.random() * 35;

        pt.x = x + (Math.random() - 0.5) * 16;
        pt.y = y + (Math.random() - 0.5) * 16;
        pt.vx = Math.cos(angle) * speed;
        pt.vy = kind === "steam" ? -Math.abs(Math.sin(angle) * speed) - 20 : Math.sin(angle) * speed;
        // Radio de 15 a 20px para el vapor térmico
        pt.radius = kind === "steam" ? 16 + Math.random() * 6 : 4 + Math.random() * 3;
        pt.kind = kind;
        pt.birthTime = nowMs;
        pt.duration = kind === "steam" ? 750 : 500;
        pt.inUse = true;
    }

    /** POWER-UPS EN LA ARENA y aros de recolección */
    private renderPowerUps(
        powerUps: PowerUpRenderable[],
        powerUpPickups: PowerUpPickupEffect[],
        width: number,
        height: number,
        nowMs: number
    ): void {
        const g = this.powerUpsGraphics;
        g.clear();

        const pwZoom = this.camera.getZoom();
        let pwPoolIndex = 0;

        for (let i = 0; i < powerUps.length; i++) {
            const pw = powerUps[i];
            const screen = this.camera.worldToScreen(pw.x, pw.y);
            const cullingMargin = 40 * pwZoom;

            if (
                screen.x + cullingMargin < 0 || screen.x - cullingMargin > width ||
                screen.y + cullingMargin < 0 || screen.y - cullingMargin > height
            ) {
                continue;
            }

            const config = POWERUP_CONFIG[pw.type];
            if (!config) continue;

            const hexCol = POWERUP_HEX_COLORS[pw.type] ?? 0xffffff;
            const pulse = Math.sin(nowMs * 0.005) * 2;
            const radius = (POWERUP_RADIUS + pulse) * pwZoom;

            g.circle(screen.x, screen.y, radius + 8 * pwZoom).fill({ color: hexCol, alpha: 0.18 });
            g.circle(screen.x, screen.y, radius + 4 * pwZoom).fill({ color: hexCol, alpha: 0.28 });
            g.circle(screen.x, screen.y, radius).fill({ color: hexCol, alpha: 0.85 });
            g.circle(screen.x, screen.y, radius).stroke({ color: 0xffffff, width: Math.max(1, 2 * pwZoom), alpha: 0.9 });

            for (let o = 0; o < 3; o++) {
                const angle = (nowMs * 0.002) + (o / 3) * Math.PI * 2;
                const ox = screen.x + Math.cos(angle) * (radius + 6 * pwZoom);
                const oy = screen.y + Math.sin(angle) * (radius + 6 * pwZoom);
                g.circle(ox, oy, Math.max(1.5, 2 * pwZoom)).fill({ color: hexCol, alpha: 0.85 });
            }

            let pView: PowerUpView;
            if (pwPoolIndex < this.powerUpPool.length) {
                pView = this.powerUpPool[pwPoolIndex];
            } else {
                pView = new PowerUpView();
                this.powerUpPool.push(pView);
                this.powerUpsContainer.addChild(pView.emojiText);
            }
            pwPoolIndex++;
            pView.inUse = true;

            if (pView.emojiText.text !== config.emoji) {
                pView.emojiText.text = config.emoji;
            }
            pView.emojiText.position.set(screen.x, screen.y);
            pView.emojiText.scale.set(Math.max(0.6, Math.min(1.4, pwZoom)));
            pView.emojiText.visible = true;
        }

        for (let j = pwPoolIndex; j < this.powerUpPool.length; j++) {
            const pView = this.powerUpPool[j];
            if (pView.inUse) {
                pView.clear();
            }
        }

        for (let i = 0; i < powerUpPickups.length; i++) {
            const eff = powerUpPickups[i];
            const elapsed = nowMs - eff.startTime;
            const progress = Math.min(1, elapsed / eff.duration);
            if (progress >= 1 || progress < 0) continue;

            const screen = this.camera.worldToScreen(eff.x, eff.y);
            const hexCol = POWERUP_HEX_COLORS[eff.type] ?? 0xffffff;
            const ringR = (POWERUP_RADIUS + progress * 40) * pwZoom;
            const alpha = 1 - progress;

            g.circle(screen.x, screen.y, ringR).stroke({
                color: hexCol,
                width: Math.max(1, 3 * (1 - progress) * pwZoom),
                alpha,
            });
            g.circle(screen.x, screen.y, ringR * 0.7).stroke({
                color: 0xffffff,
                width: Math.max(1, 1.5 * pwZoom),
                alpha: alpha * 0.8,
            });
        }
    }

    /** Calcula el tier morfológico (1 a 4) según la masa del jugador */
    private getMorphologyTier(mass: number): 1 | 2 | 3 | 4 {
        if (mass < 250) return 1;
        if (mass < 800) return 2;
        if (mass < 2000) return 3;
        return 4;
    }

    /** Renderiza entidades de jugadores con morfología de 5 elementos, propulsor alquímico y cosméticos */
    private renderPlayers(
        players: RenderablePlayer[],
        localSessionId: string | null,
        absorbPulses: Map<string, number>,
        skin: SkinId | PlayerCosmeticsConfig,
        width: number,
        height: number,
        nowMs: number,
        activeEmotes?: Map<string, { emoteId: string; startTime: number; duration: number }>
    ): void {
        const playerZoom = this.camera.getZoom();
        const count = players.length;
        const nowEpoch = Date.now();
        let activeIndex = 0;

        for (let i = 0; i < count; i++) {
            const player = players[i];
            if (!player.alive) continue;
            if (!Number.isFinite(player.renderX) || !Number.isFinite(player.renderY) || !Number.isFinite(player.mass) || player.mass <= 0) {
                continue;
            }

            let radius = Math.sqrt(player.mass) * 2 * playerZoom;

            // Pulso elástico al absorber masa
            const pulseStart = absorbPulses.get(player.sessionId);
            if (pulseStart !== undefined) {
                const pulseProgress = (nowMs - pulseStart) / 300;
                if (pulseProgress >= 1) {
                    absorbPulses.delete(player.sessionId);
                } else {
                    radius *= 1 + Math.sin(pulseProgress * Math.PI) * 0.20;
                }
            }

            const screen = this.camera.worldToScreen(player.renderX, player.renderY);
            const auraMargin = radius * 1.8;

            if (
                screen.x + auraMargin < 0 || screen.x - auraMargin > width ||
                screen.y + auraMargin < 0 || screen.y - auraMargin > height
            ) {
                continue;
            }

            let view: PlayerEntityView;
            if (activeIndex < this.entityPool.length) {
                view = this.entityPool[activeIndex];
            } else {
                view = new PlayerEntityView();
                this.entityPool.push(view);
                this.entityLayer.addChild(view.container);
            }
            activeIndex++;
            view.inUse = true;
            view.container.visible = true;

            view.thrusterGraphics.clear();
            view.auraGraphics.clear();
            view.bodyGraphics.clear();
            view.elementSkinAura.clear();
            view.cosmeticsGraphics.clear();
            view.statusGraphics.clear();
            view.titleHologramGraphics.clear();

            view.container.position.set(screen.x, screen.y);

            const speed = Math.hypot(player.vx, player.vy);
            const facingAngle = Number.isFinite(player.facingAngle) ? player.facingAngle : 0;

            const rawCosmetics = (player.isLocal && typeof skin === "object") ? skin : player.cosmetics;
            const safeCosmetics = rawCosmetics ? sanitizeCosmetics(rawCosmetics) : getBotCosmetics(player.sessionId);

            // 4. EFECTO DEL PROPULSOR ALQUÍMICO (BOOST CON BARRA ESPACIADORA)
            if (player.isBoosting) {
                const thrusterStyle = safeCosmetics.thruster || "alchemical";
                this.drawPixiAlchemicalThruster(view.thrusterGraphics, radius, facingAngle, nowMs, thrusterStyle, player.element);
            }

            // Skin Elemental Base: reemplazo de las esferas monocromáticas por la criatura elemental
            const skinId = (safeCosmetics as any)?.elementSkins?.[player.element] || getDefaultSkinForElement(player.element);
            const skinRendered = drawPixiElementSkin(view.elementSkinSprite, view.elementSkinAura, skinId, radius, player.element, nowMs, playerZoom);
            // Fallback elemental activo únicamente si la textura de la skin no está lista en GPU
            if (!skinRendered) {
                view.bodyGraphics.circle(0, 0, radius).fill({
                    color: ELEMENT_HEX_COLORS[player.element] ?? 0x38bdf8,
                    alpha: 0.88,
                });
            }

            // 4. CONTORNO DE MASA Y ALERTA DE PROXIMIDAD (COMBATE):
            // Mayor visibilidad (grosor Math.max(1.5, 2.0 * playerZoom), alpha base 0.55)
            // y alerta roja pulsante (0xef4444) si la distancia entre bordes es <= 45 * playerZoom
            const exactMassRadius = Math.sqrt(player.mass) * 2 * playerZoom;
            let isCloseToRival = false;
            const proximityThreshold = 45 * playerZoom;

            for (let j = 0; j < count; j++) {
                if (i === j) continue;
                const other = players[j];
                if (!other.alive) continue;
                if (!Number.isFinite(other.renderX) || !Number.isFinite(other.renderY) || !Number.isFinite(other.mass) || other.mass <= 0) {
                    continue;
                }
                const otherRadius = Math.sqrt(other.mass) * 2 * playerZoom;
                const maxCenterDist = exactMassRadius + otherRadius + proximityThreshold;
                const dx = (player.renderX - other.renderX) * playerZoom;
                if (Math.abs(dx) > maxCenterDist) continue;
                const dy = (player.renderY - other.renderY) * playerZoom;
                if (Math.abs(dy) > maxCenterDist) continue;

                const centerDist = Math.hypot(dx, dy);
                const edgeDist = centerDist - (exactMassRadius + otherRadius);
                if (edgeDist <= proximityThreshold) {
                    isCloseToRival = true;
                    break;
                }
            }

            const strokeWidth = Math.max(1.5, 2.0 * playerZoom);
            let strokeColor: number;
            let strokeAlpha: number;

            if (isCloseToRival) {
                strokeColor = 0xef4444;
                strokeAlpha = 0.7 + 0.3 * Math.sin(nowMs * 0.015);
            } else {
                strokeColor = ELEMENT_HEX_COLORS[player.element] ?? 0xffffff;
                strokeAlpha = 0.55;
            }

            view.bodyGraphics.circle(0, 0, exactMassRadius).stroke({
                color: strokeColor,
                alpha: strokeAlpha,
                width: strokeWidth,
            });

            this.drawPixiCosmetics(view.cosmeticsGraphics, radius, safeCosmetics, facingAngle, nowMs);
            this.drawPixiAura(view.auraGraphics, radius, safeCosmetics.aura, nowMs, player.element);

            // Renderizado de Familiar Cosmético (Mascota)
            const familiarId = safeCosmetics?.familiar;
            if (familiarId && familiarId !== "none" && player.alive) {
                view.familiarContainer.visible = true;

                const tex = getFamiliarTexture(familiarId);
                if (tex) {
                    view.familiarSprite.texture = tex;
                    view.currentFamiliarId = familiarId;
                }

                const isBuffActive = Boolean(player.petBuffActive);
                const familiarElement = getFamiliarElement(familiarId);
                const pColor = ELEMENT_HEX_COLORS[familiarElement] ?? 0x38bdf8;

                // Aro elíptico holográfico horizontal alrededor del jugador cuando el poder está activo
                if (isBuffActive) {
                    const orbitDistY = Math.max(26, radius) + 22;
                    const orbitDistX = orbitDistY * 1.35;
                    const orbitAlpha = 0.35 + Math.sin(nowMs * 0.01) * 0.2;
                    view.auraGraphics.ellipse(0, 0, orbitDistX, orbitDistY).stroke({
                        color: pColor,
                        alpha: orbitAlpha,
                        width: 2,
                    });
                }

                // Posición orbital suave relativa al jugador (órbita rápida horizontal si activo)
                const targetPos = getFamiliarTargetPosition(0, 0, radius, nowMs, isBuffActive);

                if (!view.familiarInitialized) {
                    view.familiarCurX = targetPos.x;
                    view.familiarCurY = targetPos.y;
                    view.familiarInitialized = true;
                } else {
                    const lerpFactor = 0.15; // Suavizado a 60 FPS
                    view.familiarCurX += (targetPos.x - view.familiarCurX) * lerpFactor;
                    view.familiarCurY += (targetPos.y - view.familiarCurY) * lerpFactor;
                }

                view.familiarContainer.position.set(view.familiarCurX, view.familiarCurY);

                // CORRECCIÓN DEL TAMAÑO DE LA MASCOTA Y RESPIRACIÓN MÁGICA
                const baseTargetSize = Math.max(22, Math.min(44, 30 * playerZoom));
                const breathScale = isBuffActive ? (1.0 + Math.sin(nowMs * 0.012) * 0.08) : 1.0;
                const targetSize = baseTargetSize * breathScale;
                view.familiarSprite.width = targetSize;
                view.familiarSprite.height = targetSize;
                view.familiarSprite.visible = true;

                // Emisión y actualización de estela de partículas elementales (Cap 20 max, <600ms)
                const emitCount = isBuffActive ? 3 : 1;
                view.familiarParticles.emit(0, 0, familiarElement, emitCount);
                view.familiarParticles.update(16.6);

                // RESPLANDOR Y AURA EN LA ARENA
                view.familiarTrailGraphics.clear();
                const haloAlpha = isBuffActive
                    ? Math.max(0.05, Math.min(0.95, 0.4 + Math.sin(nowMs * 0.015) * 0.35))
                    : 0.25;
                const haloRadius = (targetSize / 2) * (isBuffActive ? 1.55 : 1.35);

                view.familiarTrailGraphics.circle(0, 0, haloRadius).fill({
                    color: isBuffActive ? (familiarElement === "water" ? 0x67e8f9 : pColor) : pColor,
                    alpha: haloAlpha,
                });

                if (isBuffActive) {
                    view.familiarTrailGraphics.circle(0, 0, haloRadius * 1.25).stroke({
                        color: 0xfbbf24,
                        alpha: Math.max(0.15, haloAlpha * 0.7),
                        width: 1.5,
                    });
                }

                // Dibujar partículas en familiarTrailGraphics (destellos dorados si activo)
                const famParticles = view.familiarParticles.getParticles();
                for (let pIdx = 0; famParticles && pIdx < famParticles.length; pIdx++) {
                    const p = famParticles[pIdx];
                    view.familiarTrailGraphics.circle(p.x, p.y, p.size * Math.max(0.75, Math.min(1.25, playerZoom))).fill({
                        color: isBuffActive && pIdx % 2 === 0 ? 0xfbbf24 : p.colorHex,
                        alpha: p.alpha,
                    });
                }
            } else {
                if (view.familiarContainer.visible) {
                    view.familiarContainer.visible = false;
                    view.familiarTrailGraphics.clear();
                    view.familiarParticles.clear();
                    view.familiarInitialized = false;
                    view.currentFamiliarId = "";
                }
            }

            const isLocal = player.isLocal || (localSessionId !== null && player.sessionId === localSessionId);
            this.drawPixiStatusEffects(view.statusGraphics, player, radius, playerZoom, nowMs, nowEpoch, isLocal);

            // Títulos Honoríficos Holográficos (Sprint 35 - Hemisferio Inferior)
            if (!player.isBot && player.equippedTitle) {
                drawPlayerTitleHologram(view.titleHologramGraphics, view.titleText, player.equippedTitle, radius, playerZoom, nowMs);
            } else {
                view.titleText.visible = false;
            }

            if (radius > 12 && player.alias && player.alias.length > 0) {
                const prefix = player.killstreak >= 8 ? `👑${player.killstreak} ` : player.killstreak >= 3 ? `🔥${player.killstreak} ` : "";
                const displayAlias = `${prefix}${player.alias}`;
                if (view.aliasText.text !== displayAlias) {
                    view.aliasText.text = displayAlias;
                }
                view.aliasText.position.set(0, -radius - 6 * playerZoom);
                view.aliasText.scale.set(Math.max(0.75, Math.min(1.4, playerZoom)));
                view.aliasText.visible = true;

                if (player.isBot) {
                    view.botBadgeText.position.set(0, radius + 4 * playerZoom);
                    view.botBadgeText.scale.set(Math.max(0.7, Math.min(1.2, playerZoom)));
                    view.botBadgeText.visible = true;
                } else {
                    view.botBadgeText.visible = false;
                }
            } else {
                view.aliasText.visible = false;
                view.botBadgeText.visible = false;
            }

            // Emote social en la arena (globo de cómic con efecto neón sobre el avatar)
            const activeEmote = activeEmotes?.get(player.sessionId);
            if (activeEmote && (nowMs - activeEmote.startTime < activeEmote.duration)) {
                const elapsed = nowMs - activeEmote.startTime;
                const progress = Math.min(1, Math.max(0, elapsed / activeEmote.duration));

                // Animación pop-in (primeros 120ms: escala 0 a 1.25, luego 1.0) y fade-out (últimos 300ms: alpha 1 a 0)
                let scale = 1.0;
                if (elapsed < 120) {
                    scale = (elapsed / 120) * 1.25;
                } else if (elapsed < 200) {
                    scale = 1.25 - ((elapsed - 120) / 80) * 0.25;
                }

                let alpha = 1.0;
                const remainingMs = activeEmote.duration - elapsed;
                if (remainingMs < 300) {
                    alpha = Math.max(0, remainingMs / 300);
                }
                if (!isLocal) {
                    alpha *= 0.85;
                }

                // Posicionar centrado encima del alias
                view.emoteContainer.position.set(0, -radius - 32 * playerZoom);
                view.emoteContainer.scale.set(scale * Math.max(0.75, Math.min(1.3, playerZoom)));
                view.emoteContainer.alpha = alpha;

                // Dibujar el globo en view.emoteBubbleGraphics
                const g = view.emoteBubbleGraphics;
                g.clear();
                g.roundRect(-22, -22, 44, 44, 12).fill({ color: 0x0f172a, alpha: 0.92 }).stroke({ color: 0x38bdf8, width: 2 });
                g.poly([-5, 22, 5, 22, 0, 30]).fill({ color: 0x0f172a, alpha: 0.92 }).stroke({ color: 0x38bdf8, width: 2 });

                const emoteDef = EMOTE_CATALOG[activeEmote.emoteId] as any;
                const emojiText = emoteDef?.emoji || emoteDef?.icon || "✨";
                if (view.emoteText.text !== emojiText) {
                    view.emoteText.text = emojiText;
                }
                view.emoteContainer.visible = true;
            } else {
                view.emoteContainer.visible = false;
            }
        }

        for (let j = activeIndex; j < this.entityPool.length; j++) {
            const view = this.entityPool[j];
            if (view.inUse) {
                view.clear();
            }
        }
    }

    /** 4. EFECTO DEL PROPULSOR ALQUÍMICO (BOOST CON BARRA ESPACIADORA) */
    private drawPixiAlchemicalThruster(
        g: Graphics,
        radius: number,
        facingAngle: number,
        nowMs: number,
        thrusterStyle: ThrusterStyle = "alchemical",
        element: string = "water"
    ): void {
        if (!thrusterStyle || thrusterStyle === "none" || !Number.isFinite(radius) || radius <= 2) return;

        const backAngle = facingAngle + Math.PI;
        const cosB = Math.cos(backAngle);
        const sinB = Math.sin(backAngle);
        const perpX = -sinB;
        const perpY = cosB;
        const pal = getElementalPalette(element);

        if (thrusterStyle === "sakura") {
            const petalColors = [0xf472b6, 0xfb7185, 0xfce7f3];
            for (let p = 0; p < 8; p++) {
                const t = ((nowMs * 0.003 + p * 0.125) % 1.0);
                const sway = Math.sin(p * 4.3 + nowMs * 0.006) * (radius * 0.45);
                const pDist = radius * (1.0 + t * 2.2);
                const px = cosB * pDist + perpX * sway;
                const py = sinB * pDist + perpY * sway;
                const pSize = Math.max(2, radius * 0.08 * (1 - t * 0.3));
                const color = petalColors[p % petalColors.length];
                g.ellipse(px, py, pSize, pSize * 0.55).fill({ color, alpha: (1 - t) * 0.9 });
            }
        } else if (thrusterStyle === "cosmic") {
            for (let p = 0; p < 8; p++) {
                const t = ((nowMs * 0.0035 + p * 0.125) % 1.0);
                const spread = Math.sin(p * 3.7 + nowMs * 0.004) * (radius * 0.45);
                const pDist = radius * (1.0 + t * 2.4);
                const px = cosB * pDist + perpX * spread;
                const py = sinB * pDist + perpY * spread;
                const color = p % 2 === 0 ? 0xa855f7 : 0x38bdf8;
                const pSize = Math.max(1.8, radius * 0.065 * (1 - t * 0.5));
                g.circle(px, py, pSize).fill({ color, alpha: (1 - t) * 0.95 });
                if (p % 2 === 0) {
                    const sparkL = pSize * 1.6;
                    g.moveTo(px - sparkL, py).lineTo(px + sparkL, py)
                        .moveTo(px, py - sparkL).lineTo(px, py + sparkL)
                        .stroke({ color: 0xffffff, width: 1, alpha: (1 - t) * 0.9 });
                }
            }
        } else if (thrusterStyle === "runic") {
            for (let p = 0; p < 6; p++) {
                const t = ((nowMs * 0.0028 + p * 0.166) % 1.0);
                const sway = Math.sin(p * 3.1 + nowMs * 0.004) * (radius * 0.35);
                const pDist = radius * (1.0 + t * 2.1);
                const px = cosB * pDist + perpX * sway;
                const py = sinB * pDist + perpY * sway;
                const runeSize = Math.max(3, radius * 0.09 * (1 - t * 0.4));
                const color = p % 2 === 0 ? 0xfbbf24 : pal.secondaryHex;
                const alpha = (1 - t) * 0.95;

                if (p % 3 === 0) {
                    g.poly([
                        px, py - runeSize,
                        px + runeSize * 0.7, py,
                        px, py + runeSize,
                        px - runeSize * 0.7, py,
                    ], true).stroke({ color, width: 1.2, alpha });
                    g.moveTo(px, py - runeSize).lineTo(px, py + runeSize).stroke({ color, width: 1, alpha });
                } else if (p % 3 === 1) {
                    g.moveTo(px, py + runeSize).lineTo(px, py - runeSize)
                        .moveTo(px - runeSize * 0.6, py - runeSize * 0.3).lineTo(px, py - runeSize)
                        .lineTo(px + runeSize * 0.6, py - runeSize * 0.3)
                        .stroke({ color, width: 1.2, alpha });
                } else {
                    g.moveTo(px - runeSize * 0.5, py - runeSize)
                        .lineTo(px + runeSize * 0.5, py - runeSize * 0.3)
                        .lineTo(px - runeSize * 0.5, py + runeSize * 0.3)
                        .lineTo(px + runeSize * 0.5, py + runeSize)
                        .stroke({ color, width: 1.2, alpha });
                }
            }
        } else if (thrusterStyle === "bubbles") {
            for (let p = 0; p < 7; p++) {
                const t = ((nowMs * 0.003 + p * 0.142) % 1.0);
                const spread = Math.sin(p * 2.5 + nowMs * 0.004) * (radius * 0.4);
                const pDist = radius * (1.0 + t * 2.2);
                const px = cosB * pDist + perpX * spread;
                const py = sinB * pDist + perpY * spread;
                const bR = Math.max(2.2, radius * 0.1 * (1 - t * 0.3));
                const alpha = (1 - t) * 0.9;

                g.circle(px, py, bR).fill({ color: 0xbae6fd, alpha: alpha * 0.35 })
                    .stroke({ color: 0x38bdf8, width: 1.2, alpha });
                g.circle(px - bR * 0.35, py - bR * 0.35, Math.max(1, bR * 0.28)).fill({ color: 0xffffff, alpha });
            }
        } else if (thrusterStyle === "musical") {
            const noteColors = [0x38bdf8, 0xf43f5e, 0xfacc15, 0xa855f7];
            for (let p = 0; p < 6; p++) {
                const t = ((nowMs * 0.003 + p * 0.166) % 1.0);
                const sway = Math.sin(p * 2.7 + nowMs * 0.005) * (radius * 0.42);
                const pDist = radius * (1.0 + t * 2.2);
                const px = cosB * pDist + perpX * sway;
                const py = sinB * pDist + perpY * sway;
                const nSize = Math.max(3, radius * 0.09 * (1 - t * 0.3));
                const color = noteColors[p % noteColors.length];
                const alpha = (1 - t) * 0.95;

                g.ellipse(px, py, nSize * 0.65, nSize * 0.45).fill({ color, alpha });
                g.moveTo(px + nSize * 0.5, py)
                    .lineTo(px + nSize * 0.5, py - nSize * 1.5)
                    .stroke({ color, width: 1.4, alpha });
                g.moveTo(px + nSize * 0.5, py - nSize * 1.5)
                    .quadraticCurveTo(px + nSize * 1.1, py - nSize * 1.1, px + nSize * 1.2, py - nSize * 0.7)
                    .stroke({ color, width: 1.4, alpha });
            }
        } else if (thrusterStyle === "gold_coins") {
            for (let p = 0; p < 6; p++) {
                const t = ((nowMs * 0.0032 + p * 0.166) % 1.0);
                const sway = Math.sin(p * 2.1 + nowMs * 0.004) * (radius * 0.38);
                const pDist = radius * (1.0 + t * 2.3);
                const px = cosB * pDist + perpX * sway;
                const py = sinB * pDist + perpY * sway;
                const spin = Math.sin(nowMs * 0.008 + p);
                const cW = Math.max(1.8, radius * 0.09 * Math.abs(spin));
                const cH = Math.max(2.8, radius * 0.09);
                const alpha = (1 - t) * 0.95;

                g.ellipse(px, py, cW, cH)
                    .fill({ color: 0xfacc15, alpha })
                    .stroke({ color: 0xb45309, width: 1, alpha });
                if (p % 2 === 0) {
                    const sparkR = Math.max(1, radius * 0.035 * (1 - t));
                    g.circle(px + perpX * (radius * 0.1), py + perpY * (radius * 0.1), sparkR)
                        .fill({ color: 0xfde047, alpha });
                }
            }
        } else if (thrusterStyle === "ghost_fire") {
            const fireColors = [0x22d3ee, 0x818cf8, 0x06b6d4, 0x6366f1];
            for (let p = 0; p < 8; p++) {
                const t = ((nowMs * 0.004 + p * 0.125) % 1.0);
                const sway = Math.sin(nowMs * 0.008 + p * 1.8) * (radius * 0.35);
                const pDist = radius * (1.0 + t * 2.3);
                const px = cosB * pDist + perpX * sway;
                const py = sinB * pDist + perpY * sway;
                const fSize = Math.max(2, radius * 0.1 * (1 - t * 0.6));
                const color = fireColors[p % fireColors.length];
                g.circle(px, py, fSize).fill({ color, alpha: (1 - t) * 0.88 });
            }
        } else if (thrusterStyle === "rainbow") {
            const colors = [0xef4444, 0xf97316, 0xfacc15, 0x22c55e, 0x38bdf8];
            const wave = Math.sin(nowMs * 0.008) * (radius * 0.22);
            for (let c = 0; c < colors.length; c++) {
                const off = (c - 2) * (radius * 0.08);
                const startX = cosB * (radius * 0.95) + perpX * off;
                const startY = sinB * (radius * 0.95) + perpY * off;
                const midX = cosB * (radius * 1.55) + perpX * (off + wave);
                const midY = sinB * (radius * 1.55) + perpY * (off + wave);
                const endX = cosB * (radius * 2.25) + perpX * (off - wave);
                const endY = sinB * (radius * 2.25) + perpY * (off - wave);

                g.moveTo(startX, startY)
                    .quadraticCurveTo(midX, midY, endX, endY)
                    .stroke({ color: colors[c], width: Math.max(1.8, radius * 0.06), alpha: 0.9 });
            }
        } else {
            // alchemical default / pulse
            const pulse1 = Math.sin(nowMs * 0.04);
            const d1 = radius * (1.1 + 0.12 * pulse1);
            const cx1 = cosB * d1;
            const cy1 = sinB * d1;
            const w1 = radius * 0.65;
            const h1 = radius * 0.28;

            const pts1: number[] = [];
            for (let a = -1.0; a <= 1.0; a += 0.25) {
                const spread = Math.sin(a * Math.PI * 0.5) * w1;
                const depth = Math.cos(a * Math.PI * 0.5) * h1;
                pts1.push(cx1 + perpX * spread + cosB * depth, cy1 + perpY * spread + sinB * depth);
            }
            if (pts1.length >= 4) {
                g.moveTo(pts1[0], pts1[1]);
                for (let i = 2; i < pts1.length; i += 2) {
                    g.lineTo(pts1[i], pts1[i + 1]);
                }
                g.stroke({ color: pal.secondaryHex, width: Math.max(2, radius * 0.08), alpha: 0.9 });
            }

            const pulse2 = Math.cos(nowMs * 0.04);
            const d2 = radius * (1.35 + 0.15 * pulse2);
            const cx2 = cosB * d2;
            const cy2 = sinB * d2;
            const w2 = radius * 0.5;
            const h2 = radius * 0.22;

            const pts2: number[] = [];
            for (let a = -1.0; a <= 1.0; a += 0.25) {
                const spread = Math.sin(a * Math.PI * 0.5) * w2;
                const depth = Math.cos(a * Math.PI * 0.5) * h2;
                pts2.push(cx2 + perpX * spread + cosB * depth, cy2 + perpY * spread + sinB * depth);
            }
            if (pts2.length >= 4) {
                g.moveTo(pts2[0], pts2[1]);
                for (let i = 2; i < pts2.length; i += 2) {
                    g.lineTo(pts2[i], pts2[i + 1]);
                }
                g.stroke({ color: pal.highlightHex, width: Math.max(1.5, radius * 0.055), alpha: 0.95 });
            }

            for (let p = 0; p < 8; p++) {
                const t = ((nowMs * 0.012 + p * 0.125) % 1.0);
                const spreadAngle = (Math.sin(p * 5.3 + nowMs * 0.02) * 0.35);
                const pAngle = backAngle + spreadAngle;
                const pDist = radius * (1.0 + t * 2.2);
                const px = Math.cos(pAngle) * pDist;
                const py = Math.sin(pAngle) * pDist;
                const pSize = Math.max(1.8, radius * 0.08 * (1 - t * 0.6));
                const pColor = p % 2 === 0 ? pal.secondaryHex : pal.highlightHex;

                g.circle(px, py, pSize).fill({ color: pColor, alpha: (1 - t) * 0.95 });

                const tailLen = radius * 0.4 * (1 - t);
                g.moveTo(px - Math.cos(pAngle) * tailLen, py - Math.sin(pAngle) * tailLen)
                    .lineTo(px, py)
                    .stroke({ color: pColor, width: Math.max(1, pSize * 0.7), alpha: (1 - t) * 0.8 });
            }
        }
    }

    /**
     * 3. REDISEÑO DEL ELEMENTO AGUA (BURBUJAS INTERNAS Y GOTAS ORGÁNICAS):
     * - Cuerpo principal: Esfera de fluido azul profundo translúcido (0x0284c7 y 0x0369a1, alpha 0.92) con contorno perimetral
     *   ondulante suave (ligera deformación sinusoidal viva de 8 a 10 ondas: Math.sin(ang * 8 + nowMs * 0.004) * (radius * 0.035)).
     * - Burbujas internas: 6 a 9 burbujas esféricas (0x38bdf8 y 0x7dd3fc, alpha 0.55) con reflejo especular en medialuna
     *   blanco puro (0xffffff, alpha 0.85) flotando con balanceo orgánico interior (nowMs).
     * - Gotas externas: 8 a 12 gotas de agua deformes (radius * 0.95 a radius * 1.10) de cuerpo celeste (0x38bdf8) con punto de luz blanco.
     */
    private drawPixiWaterEntity(
        g: Graphics,
        radius: number,
        tier: number,
        facingAngle: number,
        stretch: number,
        nowMs: number,
        seed: number
    ): void {
        if (radius <= 0 || !Number.isFinite(radius)) return;

        // 3.1. Cuerpo principal de fluido azul profundo translúcido con contorno ondulante suave
        const waveCount = 8;
        const waveAmp = radius * 0.035;
        const baseR = radius * 0.86;
        const steps = 36;

        const startWave = Math.sin(nowMs * 0.004) * waveAmp;
        const startR = baseR + startWave;
        g.moveTo(startR, 0);

        for (let s = 1; s <= steps; s++) {
            const ang = (s / steps) * Math.PI * 2;
            const wave = Math.sin(ang * waveCount + nowMs * 0.004) * waveAmp;
            const rFluid = baseR + wave;
            g.lineTo(Math.cos(ang) * rFluid, Math.sin(ang) * rFluid);
        }
        g.closePath();

        // Relleno de fluido azul profundo translúcido (0x0369a1 y 0x0284c7, alpha 0.92)
        g.fill({ color: 0x0369a1, alpha: 0.92 });
        g.stroke({
            color: 0x38bdf8,
            width: Math.max(1.2, radius * 0.025),
            alpha: 0.70,
        });

        // Capa esférica interna líquida brillante
        g.circle(0, 0, radius * 0.68).fill({ color: 0x0284c7, alpha: 0.92 });
        g.circle(0, 0, radius * 0.46).fill({ color: 0x0369a1, alpha: 0.60 });

        // Reflejo especular superior del orbe líquido (brillo vítreo de agua cristalina)
        g.circle(-radius * 0.22, -radius * 0.24, radius * 0.18).fill({ color: 0xffffff, alpha: 0.60 });
        g.circle(-radius * 0.26, -radius * 0.28, radius * 0.09).fill({ color: 0xffffff, alpha: 0.90 });

        // 3.2. BURBUJAS INTERNAS: 6 a 9 burbujitas esféricas con reflejo especular en medialuna blanco puro
        const bubbleCount = 7 + (tier >= 2 ? 1 : 0); // 7 a 8 burbujas (en el rango de 6 a 9)
        for (let b = 0; b < bubbleCount; b++) {
            const baseAngle = (b / bubbleCount) * Math.PI * 2 + (seed % 13) * 0.5;
            // Balanceo orgánico sutil interior
            const swayX = Math.sin(nowMs * 0.0022 + b * 2.3) * (radius * 0.06);
            const swayY = Math.cos(nowMs * 0.0028 + b * 1.9) * (radius * 0.06);
            const distFromCenter = radius * (0.20 + ((b * 4) % 6) * 0.08); // completamente contenidas dentro del cuerpo
            const bx = Math.cos(baseAngle) * distFromCenter + swayX;
            const by = Math.sin(baseAngle) * distFromCenter + swayY;

            const bubbleR = Math.max(2.0, radius * (0.065 + ((b % 3) * 0.022)));
            const bubbleColor = b % 2 === 0 ? 0x38bdf8 : 0x7dd3fc;

            // Esfera translúcida de la burbuja (alpha 0.55)
            g.circle(bx, by, bubbleR).fill({ color: bubbleColor, alpha: 0.55 });
            g.circle(bx, by, bubbleR).stroke({ color: 0xffffff, width: 1, alpha: 0.40 });

            // Reflejo especular en medialuna blanco puro (0xffffff, alpha 0.85)
            const specOffset = bubbleR * 0.35;
            const specR = Math.max(0.8, bubbleR * 0.32);
            g.circle(bx - specOffset, by - specOffset, specR).fill({ color: 0xffffff, alpha: 0.85 });
            // Punto secundario tenue opuesto para profundidad cristalina
            g.circle(bx + specOffset * 0.5, by + specOffset * 0.5, Math.max(0.6, specR * 0.45)).fill({
                color: 0xffffff,
                alpha: 0.50,
            });
        }

        // 3.3. GOTAS EXTERNAS: 8 a 12 gotas de agua deformes orbitando cerca del borde (0.95x a 1.10x radius)
        const dropletCount = 10; // 10 gotas (en el rango de 8 a 12)
        for (let d = 0; d < dropletCount; d++) {
            const dAng = nowMs * 0.0009 + (d / dropletCount) * Math.PI * 2 + Math.sin(nowMs * 0.002 + d * 1.7) * 0.15;
            const dDist = radius * (0.97 + ((d % 3) * 0.04) + Math.sin(nowMs * 0.003 + d * 2.1) * 0.03); // de 0.95x a 1.08x radius
            const dx = Math.cos(dAng) * dDist;
            const dy = Math.sin(dAng) * dDist;

            // Forma orgánica redondeada y elíptica oscilante (tensión superficial natural)
            const dropR = Math.max(1.8, radius * (0.038 + ((d % 4) * 0.014)));
            const deformTime = nowMs * 0.005 + d * 1.6;
            const deformScale = 0.28 * Math.sin(deformTime);
            const rx = dropR * (1 + deformScale);
            const ry = dropR * (1 - deformScale);

            // Cuerpo celeste (0x38bdf8)
            g.ellipse(dx, dy, rx, ry).fill({ color: 0x38bdf8, alpha: 0.90 });
            g.ellipse(dx, dy, rx, ry).stroke({ color: 0x7dd3fc, width: 1, alpha: 0.50 });

            // Pequeño punto de luz blanco reflectante (0xffffff)
            const shineOffset = dropR * 0.30;
            g.circle(dx - shineOffset, dy - shineOffset, Math.max(0.8, dropR * 0.30)).fill({
                color: 0xffffff,
                alpha: 0.95,
            });
        }
    }

    /**
     * 3. ELEMENTO FUEGO (ESTRICTAMENTE CONTENIDO DENTRO DEL LÍMITE DE MASA):
     * - Corona exterior naranja-rojiza: base en 0.65x radius, puntas alcanzando como máximo entre 0.92x y 1.0x radius.
     * - Capa intermedia de llamas amarillas: de 0.50x a 0.65x radius.
     * - Núcleo incandescente central: de 0.28x a 0.35x radius.
     * - Ninguna llama o pico sobrepasa el aro perimetral de masa (radius).
     */
    private drawPixiFireEntity(
        g: Graphics,
        radius: number,
        tier: number,
        facingAngle: number,
        stretch: number,
        nowMs: number,
        seed: number
    ): void {
        if (radius <= 0 || !Number.isFinite(radius)) return;

        const tailAngle = facingAngle + Math.PI;

        // Corona exterior: 18 a 22 lenguas de fuego naranja-rojizas (base en 0.65x radius)
        const tongueCount = 18 + Math.min(4, tier * 2);
        const rValley = radius * 0.65;

        for (let i = 0; i < tongueCount; i++) {
            const aBase = (i / tongueCount) * Math.PI * 2;
            const aNext = ((i + 1) / tongueCount) * Math.PI * 2;
            const aMid = (aBase + aNext) * 0.5;

            // Oscilación sinusoidal contenida en los ápices para respiración y crepitar orgánico
            const flicker1 = Math.sin(nowMs * 0.009 + i * 2.1 + (seed % 17)) * (radius * 0.035);
            const flicker2 = Math.cos(nowMs * 0.016 - i * 1.4) * (radius * 0.02);

            // Estirar sutilmente hacia atrás al desplazarse, sin sobrepasar el círculo de masa
            const align = Math.cos(aMid - tailAngle);
            const tailBoost = align > 0 && stretch > 0.02
                ? align * stretch * 0.25 * radius
                : 0;

            // Puntas más largas alcanzan como máximo entre 0.92x radius y 1.0x radius (estrictamente <= radius)
            const rawTip = radius * 0.93 + tailBoost + flicker1 + flicker2;
            const rTip = Math.min(radius * 0.98, Math.max(radius * 0.76, rawTip));

            // Curvatura rotatoria orgánica
            const curlAngle = aMid + 0.08;

            const vx = Math.cos(aBase) * rValley;
            const vy = Math.sin(aBase) * rValley;
            const tx = Math.cos(curlAngle) * rTip;
            const ty = Math.sin(curlAngle) * rTip;

            // Curva Bézier desde la base hasta la punta curvada
            const cpx1 = Math.cos(aBase + 0.05) * (rValley + (rTip - rValley) * 0.65);
            const cpy1 = Math.sin(aBase + 0.05) * (rValley + (rTip - rValley) * 0.65);

            if (i === 0) {
                g.moveTo(vx, vy);
            }
            g.quadraticCurveTo(cpx1, cpy1, tx, ty);

            // Caída pronunciada hacia el siguiente valle
            const cpx2 = Math.cos(aNext - 0.02) * (rValley + (rTip - rValley) * 0.25);
            const cpy2 = Math.sin(aNext - 0.02) * (rValley + (rTip - rValley) * 0.25);
            const nx = Math.cos(aNext) * rValley;
            const ny = Math.sin(aNext) * rValley;
            g.quadraticCurveTo(cpx2, cpy2, nx, ny);
        }

        g.closePath();
        g.fill({ color: 0xea580c, alpha: 0.95 });
        g.stroke({ color: 0xdc2626, width: Math.max(1.5, radius * 0.035), alpha: 0.92 });

        // Capa intermedia de llamas amarillas: de 0.50x a 0.65x radius
        const innerTongueCount = 14 + tier;
        const rInnerValley = radius * 0.50;

        for (let j = 0; j < innerTongueCount; j++) {
            const aBase = (j / innerTongueCount) * Math.PI * 2;
            const aNext = ((j + 1) / innerTongueCount) * Math.PI * 2;
            const aMid = (aBase + aNext) * 0.5;

            const flicker = Math.sin(nowMs * -0.012 + j * 2.7) * (radius * 0.025);
            const align = Math.cos(aMid - tailAngle);
            const tailBoost = align > 0 && stretch > 0.02 ? align * stretch * 0.15 * radius : 0;
            const rawInnerTip = radius * 0.59 + tailBoost + flicker;
            const rInnerTip = Math.min(radius * 0.65, Math.max(radius * 0.52, rawInnerTip));
            const curlAngle = aMid + 0.06;

            const vx = Math.cos(aBase) * rInnerValley;
            const vy = Math.sin(aBase) * rInnerValley;
            const tx = Math.cos(curlAngle) * rInnerTip;
            const ty = Math.sin(curlAngle) * rInnerTip;

            const cpx1 = Math.cos(aBase + 0.04) * (rInnerValley + (rInnerTip - rInnerValley) * 0.6);
            const cpy1 = Math.sin(aBase + 0.04) * (rInnerValley + (rInnerTip - rInnerValley) * 0.6);

            if (j === 0) {
                g.moveTo(vx, vy);
            }
            g.quadraticCurveTo(cpx1, cpy1, tx, ty);

            const cpx2 = Math.cos(aNext - 0.02) * (rInnerValley + (rInnerTip - rInnerValley) * 0.2);
            const cpy2 = Math.sin(aNext - 0.02) * (rInnerValley + (rInnerTip - rInnerValley) * 0.2);
            const nx = Math.cos(aNext) * rInnerValley;
            const ny = Math.sin(aNext) * rInnerValley;
            g.quadraticCurveTo(cpx2, cpy2, nx, ny);
        }

        g.closePath();
        g.fill({ color: 0xfacc15, alpha: 0.98 });
        g.stroke({ color: 0xfef08a, width: Math.max(1.2, radius * 0.03), alpha: 0.9 });

        // Núcleo incandescente central: de 0.28x a 0.35x radius
        g.circle(0, 0, radius * 0.35).fill({ color: 0xfef08a, alpha: 0.98 });
        g.circle(0, 0, radius * 0.22).fill({ color: 0xffffff, alpha: 1.0 });

        // Chispas de plasma que se desprenden contenidas dentro del límite de masa
        for (let s = 0; s < 8; s++) {
            const t = ((nowMs * 0.0028 + s * 0.125 + (seed % 7) * 0.1) % 1.0);
            const sparkAng = tailAngle + Math.sin(s * 4.3 + nowMs * 0.006) * 0.65;
            const sparkDist = radius * (0.60 + t * 0.35); // max 0.95 * radius
            const sx = Math.cos(sparkAng) * sparkDist;
            const sy = Math.sin(sparkAng) * sparkDist;
            const sparkR = Math.max(1.4, radius * 0.045 * (1 - t * 0.7));
            const col = t < 0.35 ? 0xffffff : (t < 0.7 ? 0xfef08a : 0xf97316);

            g.circle(sx, sy, sparkR).fill({ color: col, alpha: (1 - t) * 0.95 });
        }
    }

    /** 2. TIERRA: Base de terracota cálida/umber oscuro y cristales de ámbar/bronce resplandeciente */
    private drawPixiEarthEntity(
        g: Graphics,
        radius: number,
        tier: number,
        nowMs: number,
        seed: number
    ): void {
        if (radius <= 0 || !Number.isFinite(radius)) return;

        // Base y núcleo de magma terroso cálido (terracota oscura 0x5c2c16 / umber cálido 0x78350f)
        g.circle(0, 0, radius * 0.88).fill({ color: 0x5c2c16, alpha: 0.96 });
        g.circle(0, 0, radius * 0.65).fill({ color: 0x78350f, alpha: 0.98 });
        g.circle(0, 0, radius * 0.38).fill({ color: 0xb45309, alpha: 1.0 });

        // Placas tectónicas de obsidiana flotantes con rotación lenta
        const plateCount = 7 + tier * 2;
        const rot = nowMs * 0.00035;

        for (let i = 0; i < plateCount; i++) {
            const ang = rot + (i / plateCount) * Math.PI * 2;
            const noise = Math.sin(seed + i * 7.13) * 0.12;
            const dist = radius * (0.52 + noise * 0.4);
            const px = Math.cos(ang) * dist;
            const py = Math.sin(ang) * dist;
            const pSize = Math.max(3, radius * (0.28 + (i % 3) * 0.05));

            const platePts = [
                px - pSize * 0.9, py - pSize * 0.5,
                px + pSize * 0.2, py - pSize * 0.9,
                px + pSize * 0.9, py - pSize * 0.2,
                px + pSize * 0.6, py + pSize * 0.8,
                px - pSize * 0.7, py + pSize * 0.6,
            ];

            g.poly(platePts, true)
                .fill({ color: 0x1c1917, alpha: 0.95 })
                .stroke({ color: 0x5c2c16, width: Math.max(1, radius * 0.035) });
        }

        // Grietas volcánicas ardientes de ámbar fundido entre placas
        for (let k = 0; k < 5; k++) {
            const kAng = rot * 1.5 + (k * Math.PI * 2) / 5;
            const kx1 = Math.cos(kAng) * radius * 0.2;
            const ky1 = Math.sin(kAng) * radius * 0.2;
            const kx2 = Math.cos(kAng + 0.3) * radius * 0.75;
            const ky2 = Math.sin(kAng + 0.3) * radius * 0.75;

            g.moveTo(kx1, ky1).lineTo(kx2, ky2).stroke({
                color: 0xd97706,
                width: Math.max(1.2, radius * 0.04),
                alpha: 0.88,
            });
        }

        // Cristales minerales en órbita: café ámbar brillante (0xd97706) y bronce resplandeciente (0xb45309)
        const crystalCount = 4 + tier * 2;
        for (let c = 0; c < crystalCount; c++) {
            const cAng = nowMs * 0.0008 * (c % 2 === 0 ? 1 : -1) + (c * Math.PI * 2) / crystalCount;
            const cDist = radius * (0.95 + Math.sin(nowMs * 0.004 + c) * 0.1);
            const cx = Math.cos(cAng) * cDist;
            const cy = Math.sin(cAng) * cDist;
            const cSize = Math.max(2, radius * 0.08);

            const crystalPts = [
                cx, cy - cSize * 1.2,
                cx + cSize * 0.7, cy,
                cx, cy + cSize * 1.2,
                cx - cSize * 0.7, cy,
            ];
            const bronzeColor = c % 2 === 0 ? 0xd97706 : 0xb45309;
            g.poly(crystalPts, true).fill({ color: bronzeColor, alpha: 0.95 });
            g.poly(crystalPts, true).stroke({ color: 0xf59e0b, width: 1, alpha: 0.7 });
        }
    }

    /**
     * 4. REDISEÑO DEL ELEMENTO PLANTA / VIENTO (ESTILO IMAGEN 4):
     * Esfera base verde fuerte profundo con núcleo bio-luminiscente esmeralda, envuelta por
     * 7 a 9 franjas dinámicas de viento y ráfagas en vórtice espiral con puntas afiladas y esporas.
     */
    private drawPixiPlantEntity(
        g: Graphics,
        radius: number,
        tier: number,
        nowMs: number,
        seed: number
    ): void {
        if (radius <= 0 || !Number.isFinite(radius)) return;

        const breathe = 1 + Math.sin(nowMs * 0.003 + (seed % 15)) * 0.025;
        const r = radius * breathe;

        // 4.1. Esfera base de color verde bosque oscuro profundo con núcleo esmeralda
        g.circle(0, 0, r * 0.78).fill({ color: 0x14532d, alpha: 0.96 }); // Verde bosque umbrío
        g.circle(0, 0, r * 0.65).fill({ color: 0x166534, alpha: 0.98 }); // Verde bosque profundo
        g.circle(0, 0, r * 0.50).fill({ color: 0x15803d, alpha: 0.98 }); // Verde esmeralda intenso
        g.circle(0, 0, r * 0.35).fill({ color: 0x16a34a, alpha: 1.0 });  // Núcleo esmeralda
        // Reflejo esmeralda superior (sin verde menta pastel)
        g.circle(-r * 0.14, -r * 0.16, r * 0.18).fill({ color: 0x15803d, alpha: 0.55 });
        g.circle(-r * 0.18, -r * 0.20, r * 0.08).fill({ color: 0xffffff, alpha: 0.95 });

        // 4.2. Corona de hojas perimetrales más pequeñas abrazando de cerca el orbe (punta <= radius * 1.12)
        const leafCount = 8 + (tier >= 2 ? 2 : 0);
        const rot = nowMs * 0.0009;

        for (let i = 0; i < leafCount; i++) {
            const baseAng = rot + (i / leafCount) * Math.PI * 2;
            const span = 0.55 + ((i % 3) * 0.06);
            const windFlutter = Math.sin(nowMs * 0.005 + i * 2.3) * (radius * 0.015);

            // Coordenadas de la hoja: nace en el orbe y abraza de cerca el perímetro
            const rInner = radius * 0.72;
            // Punta exterior no debe exceder radius * 1.12
            const rTip = Math.min(radius * 1.11, radius * (1.05 + ((i % 2) * 0.03)) + windFlutter);
            const rTail = radius * 0.75;

            const xStart = Math.cos(baseAng) * rInner;
            const yStart = Math.sin(baseAng) * rInner;

            const midAng = baseAng + span * 0.5;
            const cpOuterX = Math.cos(midAng) * Math.min(radius * 1.12, rTip * 1.02);
            const cpOuterY = Math.sin(midAng) * Math.min(radius * 1.12, rTip * 1.02);

            const tipAng = baseAng + span;
            const xTip = Math.cos(tipAng) * rTip;
            const yTip = Math.sin(tipAng) * rTip;

            const cpInnerX = Math.cos(midAng) * (rInner * 1.04);
            const cpInnerY = Math.sin(midAng) * (rInner * 1.04);

            const xEnd = Math.cos(baseAng + 0.12) * rTail;
            const yEnd = Math.sin(baseAng + 0.12) * rTail;

            // Trazar hoja abrazando el orbe
            g.moveTo(xStart, yStart);
            g.quadraticCurveTo(cpOuterX, cpOuterY, xTip, yTip);
            g.quadraticCurveTo(cpInnerX, cpInnerY, xEnd, yEnd);
            g.closePath();

            // Relleno base de las hojas: verde bosque/selva oscuro profundo (0x14532d y 0x166534)
            const leafFillColor = i % 2 === 0 ? 0x14532d : 0x166534;
            g.fill({ color: leafFillColor, alpha: 0.95 });

            // Nervadura o borde sutil: verde esmeralda 0x15803d (eliminar por completo verde menta pastel)
            g.stroke({
                color: 0x15803d,
                width: Math.max(1.2, Math.min(1.8, 1.2 + (radius / 60) * 0.4)),
                alpha: 0.90,
            });

            // Nervadura central sutil hacia el ápice
            const veinMidX = Math.cos(midAng) * (rInner * 1.02);
            const veinMidY = Math.sin(midAng) * (rInner * 1.02);
            g.moveTo(xStart, yStart);
            g.quadraticCurveTo(veinMidX, veinMidY, xTip * 0.95, yTip * 0.95);
            g.stroke({
                color: 0x15803d,
                width: 1,
                alpha: 0.70,
            });
        }

        // 4.3. Motas de esporas y polen esmeralda oscuro flotando suavemente alrededor del orbe
        const sporeCount = 6 + tier * 2;
        for (let s = 0; s < sporeCount; s++) {
            const sAng = -nowMs * 0.0007 + (s * Math.PI * 2) / sporeCount;
            const sDist = Math.min(radius * 1.12, radius * (1.02 + Math.sin(nowMs * 0.003 + s * 2.1) * 0.08));
            const sx = Math.cos(sAng) * sDist;
            const sy = Math.sin(sAng) * sDist;
            const sporeR = Math.max(1.4, radius * (0.035 + (s % 3) * 0.015));

            const sporeColor = s % 2 === 0 ? 0x14532d : 0x166534;
            g.circle(sx, sy, sporeR).fill({ color: sporeColor, alpha: 0.88 });
            g.circle(sx, sy, Math.max(1, sporeR * 0.5)).fill({ color: 0x15803d, alpha: 0.90 });
        }
    }

    /**
     * 3. ELEMENTO ELÉCTRICO (ESTRICTAMENTE CONTENIDO DENTRO DEL LÍMITE DE MASA):
     * Orbe solar dorado con halo perimetral y relámpagos fractales aleatorios en zigzag
     * que nacen en radius * 0.20 y cuya punta más extrema alcanza como máximo entre 0.88x y 0.98x radius,
     * estrictamente dentro del círculo de masa perimetral.
     */
    private drawPixiElectricEntity(
        g: Graphics,
        radius: number,
        tier: number,
        nowMs: number,
        seed: number
    ): void {
        if (radius <= 0 || !Number.isFinite(radius)) return;

        // 3.1. Aro de choque y halo de energía resplandeciente en el perímetro del orbe
        const haloPulse = 0.82 + 0.18 * Math.sin(nowMs * 0.01);
        g.circle(0, 0, radius * 0.86).fill({ color: 0xfacc15, alpha: 0.22 * haloPulse });
        g.circle(0, 0, radius * 0.78).fill({ color: 0xfef08a, alpha: 0.32 * haloPulse });

        // Aro de choque eléctrico brillante
        g.circle(0, 0, radius * 0.74).stroke({
            color: 0xfde047,
            width: Math.max(2, radius * 0.05),
            alpha: 0.92 * haloPulse,
        });
        g.circle(0, 0, radius * 0.70).stroke({
            color: 0xffffff,
            width: Math.max(1.2, radius * 0.03),
            alpha: 0.98,
        });

        // 3.2. Núcleo esférico solar dorado brillante (gradiente concéntrico)
        g.circle(0, 0, radius * 0.68).fill({ color: 0xf59e0b, alpha: 0.96 }); // Oro solar profundo
        g.circle(0, 0, radius * 0.56).fill({ color: 0xfacc15, alpha: 0.98 }); // Oro radiante
        g.circle(0, 0, radius * 0.40).fill({ color: 0xfef08a, alpha: 1.0 });  // Amarillo incandescente
        const whiteCoreStrobe = 0.92 + 0.08 * Math.sin(nowMs * 0.024);
        g.circle(0, 0, radius * 0.22 * whiteCoreStrobe).fill({ color: 0xffffff, alpha: 1.0 }); // Centro blanco nuclear

        // 3.3. Grietas y fracturas de plasma superficiales en telaraña eléctrica
        const tick = Math.floor(nowMs / 28);
        const crackArms = 7;
        const crackNodes: { x: number; y: number }[] = [];

        for (let c = 0; c < crackArms; c++) {
            const baseCrackAng = (c / crackArms) * Math.PI * 2 + ((tick * 17 + c * 31) % 100) * 0.008;
            let curX = Math.cos(baseCrackAng) * (radius * 0.12);
            let curY = Math.sin(baseCrackAng) * (radius * 0.12);

            g.moveTo(curX, curY);

            const segments = 3;
            for (let s = 1; s <= segments; s++) {
                const frac = s / segments;
                const jitter = Math.sin(seed + c * 23 + s * 37 + tick * 19) * 0.28;
                const segAng = baseCrackAng + jitter;
                const segDist = radius * (0.15 + frac * 0.52);
                const nx = Math.cos(segAng) * segDist;
                const ny = Math.sin(segAng) * segDist;

                g.lineTo(nx, ny);

                if (s === 2) {
                    crackNodes.push({ x: nx, y: ny });
                }
                curX = nx;
                curY = ny;
            }
            g.stroke({
                color: c % 2 === 0 ? 0xffffff : 0xfef08a,
                width: Math.max(1.4, radius * 0.038),
                alpha: 0.95,
            });
        }

        // Conexiones transversales de telaraña de plasma
        for (let n = 0; n < crackNodes.length; n++) {
            const nextN = (n + 1) % crackNodes.length;
            if (n % 2 === 0) {
                g.moveTo(crackNodes[n].x, crackNodes[n].y)
                    .lineTo(crackNodes[nextN].x, crackNodes[nextN].y)
                    .stroke({
                        color: 0xffffff,
                        width: Math.max(1, radius * 0.025),
                        alpha: 0.88,
                    });
            }
        }

        // 3.4. Relámpagos fractales aleatorios en zigzag estrictamente contenidos (0.88x a 0.98x radio)
        const boltCount = 14 + tier * 2; // 14 a 18 relámpagos dinámicos
        const boltColors = [0xfacc15, 0xfde047, 0xfef08a, 0xffffff];

        for (let b = 0; b < boltCount; b++) {
            const baseAng = (b / boltCount) * Math.PI * 2 + ((tick * 13 + b * 29) % 100) * 0.007;
            // Alcance máximo entre 0.88x radius y 0.98x radius
            const reachFactor = 0.88 + (Math.abs(Math.sin(seed + b * 17.3 + tick * 0.25)) * 0.09); // Entre 0.88 y 0.97
            const reach = radius * reachFactor;
            const segments = 4 + (b % 3); // 4 a 6 segmentos quebrados

            // Alternar grosores (1.5px a 3.5px) y colores en amarillo fuerte, oro brillante y blanco incandescente
            const lineWidth = Math.max(1.5, Math.min(3.5, 1.5 + (b % 3) * 1.0));
            const boltColor = boltColors[b % boltColors.length];

            // Nace en radius * 0.20
            let curX = Math.cos(baseAng) * (radius * 0.20);
            let curY = Math.sin(baseAng) * (radius * 0.20);
            g.moveTo(curX, curY);

            for (let s = 1; s <= segments; s++) {
                const frac = s / segments;
                const jitter = Math.sin(seed + b * 37 + s * 19 + tick * 41) * (0.25 + frac * 0.12);
                const segAng = baseAng + jitter;
                const segDist = radius * 0.20 + frac * (reach - radius * 0.20);
                const nx = Math.cos(segAng) * segDist;
                const ny = Math.sin(segAng) * segDist;

                g.lineTo(nx, ny);

                // Bifurcación orgánica que brota en segmentos intermedios (contenida dentro de masa)
                if (s === 3 && (tier >= 2 || b % 2 === 0)) {
                    const forkSign = (b + s) % 2 === 0 ? 1 : -1;
                    const forkAng = segAng + forkSign * (0.35 + Math.sin(tick * 0.3 + b) * 0.10);
                    const forkReach = Math.min(radius * 0.96, segDist + radius * 0.12);
                    const fx = Math.cos(forkAng) * forkReach;
                    const fy = Math.sin(forkAng) * forkReach;

                    g.moveTo(nx, ny);
                    g.lineTo(fx, fy);
                    g.moveTo(nx, ny);
                }

                curX = nx;
                curY = ny;
            }

            // Trazo continuo y afilado de alta energía sin bolitas terminales
            g.stroke({ color: boltColor, width: lineWidth, alpha: 0.95 });
        }
    }

    /** Dibuja accesorios cosméticos modulares sobre el cuerpo (tatuajes, ojos, gafas y boca) */
    private drawPixiCosmetics(
        g: Graphics,
        radius: number,
        cosmetics: PlayerCosmeticsConfig,
        facingAngle: number,
        nowMs: number
    ): void {
        if (!Number.isFinite(radius) || radius <= 2) return;

        // --- TATUAJES (10) ---
        if (cosmetics.tattoo && cosmetics.tattoo !== "none") {
            if (cosmetics.tattoo === "runes") {
                const pulse = 0.55 + Math.sin(nowMs * 0.004) * 0.35;
                g.circle(0, 0, radius * 0.62).stroke({ color: 0x38bdf8, width: Math.max(1.5, radius * 0.06), alpha: pulse });
                for (let i = 0; i < 4; i++) {
                    const ang = (i * Math.PI) / 2 + nowMs * 0.0008;
                    g.moveTo(Math.cos(ang) * radius * 0.45, Math.sin(ang) * radius * 0.45)
                        .lineTo(Math.cos(ang) * radius * 0.78, Math.sin(ang) * radius * 0.78)
                        .stroke({ color: 0x38bdf8, width: Math.max(1.5, radius * 0.06), alpha: pulse });
                }
            } else if (cosmetics.tattoo === "cracks") {
                g.moveTo(-radius * 0.45, -radius * 0.2)
                    .lineTo(-radius * 0.1, radius * 0.05)
                    .lineTo(radius * 0.2, -radius * 0.15)
                    .lineTo(radius * 0.45, radius * 0.25)
                    .stroke({ color: 0xef4444, width: Math.max(1.5, radius * 0.055), alpha: 0.8 });
            } else if (cosmetics.tattoo === "tribal") {
                for (const sign of [-1, 1]) {
                    const tx1 = sign * radius * 0.55;
                    const ty1 = -radius * 0.3;
                    const tx2 = sign * radius * 0.25;
                    const ty2 = radius * 0.35;
                    g.moveTo(tx1, ty1).quadraticCurveTo(sign * radius * 0.7, 0, tx2, ty2)
                        .stroke({ color: 0xf472b6, width: Math.max(1.5, radius * 0.06), alpha: 0.85 });
                }
            } else if (cosmetics.tattoo === "dragon") {
                g.moveTo(-radius * 0.45, radius * 0.4)
                    .quadraticCurveTo(-radius * 0.5, -radius * 0.2, 0, -radius * 0.35)
                    .quadraticCurveTo(radius * 0.5, -radius * 0.45, radius * 0.35, 0.1 * radius)
                    .quadraticCurveTo(radius * 0.2, 0.45 * radius, -0.1 * radius, 0.3 * radius)
                    .stroke({ color: 0xf59e0b, width: Math.max(1.8, radius * 0.065), alpha: 0.85 });
                g.circle(-radius * 0.45, radius * 0.4, Math.max(2, radius * 0.08)).fill({ color: 0xef4444, alpha: 0.9 });
            } else if (cosmetics.tattoo === "circuit") {
                const pulse = 0.7 + 0.3 * Math.sin(nowMs * 0.005);
                g.moveTo(-radius * 0.5, -radius * 0.2)
                    .lineTo(-radius * 0.2, -radius * 0.2)
                    .lineTo(-radius * 0.2, radius * 0.3)
                    .lineTo(radius * 0.3, radius * 0.3)
                    .lineTo(radius * 0.3, radius * 0.45)
                    .stroke({ color: 0x22c55e, width: Math.max(1.5, radius * 0.05), alpha: pulse });
                g.moveTo(radius * 0.4, -radius * 0.4)
                    .lineTo(radius * 0.1, -radius * 0.4)
                    .lineTo(radius * 0.1, -radius * 0.1)
                    .stroke({ color: 0x22c55e, width: Math.max(1.5, radius * 0.05), alpha: pulse });
                for (const [nx, ny] of [
                    [-radius * 0.5, -radius * 0.2],
                    [radius * 0.3, radius * 0.45],
                    [radius * 0.4, -radius * 0.4],
                ]) {
                    g.circle(nx, ny, Math.max(2, radius * 0.055)).fill({ color: 0x86efac, alpha: 0.95 });
                }
            } else if (cosmetics.tattoo === "flame_mark") {
                const pts = [
                    0, radius * 0.5,
                    -radius * 0.25, radius * 0.15,
                    -radius * 0.18, -radius * 0.28,
                    0, -radius * 0.46,
                    radius * 0.18, -radius * 0.28,
                    radius * 0.25, radius * 0.15,
                ];
                g.poly(pts, true).fill({ color: 0xef4444, alpha: 0.75 }).stroke({ color: 0xf59e0b, width: Math.max(1.2, radius * 0.04) });
            } else if (cosmetics.tattoo === "celestial") {
                const stars = [
                    [-radius * 0.35, -radius * 0.35],
                    [-radius * 0.1, -radius * 0.45],
                    [radius * 0.25, -radius * 0.3],
                    [radius * 0.35, 0],
                    [radius * 0.1, radius * 0.35],
                    [-radius * 0.25, radius * 0.25],
                ];
                g.moveTo(stars[0][0], stars[0][1]);
                for (let i = 1; i < stars.length; i++) {
                    g.lineTo(stars[i][0], stars[i][1]);
                }
                g.stroke({ color: 0xc084fc, width: Math.max(1.2, radius * 0.035), alpha: 0.75 });
                for (const [sx, sy] of stars) {
                    g.circle(sx, sy, Math.max(1.5, radius * 0.045)).fill({ color: 0xffffff });
                }
            } else if (cosmetics.tattoo === "curse") {
                for (const s of [-1, 1]) {
                    g.moveTo(s * radius * 0.25, radius * 0.05)
                        .quadraticCurveTo(s * radius * 0.48, radius * 0.15, s * radius * 0.42, radius * 0.4)
                        .stroke({ color: 0x581c87, width: Math.max(1.8, radius * 0.065), alpha: 0.85 });
                    g.moveTo(s * radius * 0.22, radius * 0.18)
                        .lineTo(s * radius * 0.48, radius * 0.25)
                        .stroke({ color: 0x581c87, width: Math.max(1.8, radius * 0.065), alpha: 0.85 });
                }
                g.circle(0, -radius * 0.42, Math.max(2, radius * 0.07)).fill({ color: 0x9333ea, alpha: 0.9 });
            } else if (cosmetics.tattoo === "alchemic_circle") {
                const pulse = 0.65 + Math.sin(nowMs * 0.003) * 0.25;
                g.circle(0, 0, radius * 0.58).stroke({ color: 0xf59e0b, width: Math.max(1.2, radius * 0.04), alpha: pulse });
                g.circle(0, 0, radius * 0.46).stroke({ color: 0xf59e0b, width: Math.max(1.2, radius * 0.04), alpha: pulse });
                const tri1: number[] = [];
                const tri2: number[] = [];
                for (let i = 0; i < 3; i++) {
                    const a1 = (i * Math.PI * 2) / 3 - Math.PI / 2;
                    tri1.push(Math.cos(a1) * radius * 0.46, Math.sin(a1) * radius * 0.46);
                    const a2 = (i * Math.PI * 2) / 3 + Math.PI / 2;
                    tri2.push(Math.cos(a2) * radius * 0.46, Math.sin(a2) * radius * 0.46);
                }
                g.poly(tri1, true).stroke({ color: 0xf59e0b, width: Math.max(1, radius * 0.035), alpha: pulse });
                g.poly(tri2, true).stroke({ color: 0xf59e0b, width: Math.max(1, radius * 0.035), alpha: pulse });
            }
        }

        const lookX = Math.cos(facingAngle) * (radius * 0.10);
        const lookY = Math.sin(facingAngle) * (radius * 0.10);
        const eyeY = -radius * 0.10 + lookY * 0.5;
        const eyeSpacing = radius * 0.31;
        const eyeR = Math.max(2.5, radius * 0.19);

        // --- OJOS (10) ---
        if (cosmetics.eyes === "cyclops") {
            const cx = lookX * 0.6;
            const cy = eyeY;
            const bigR = eyeR * 1.45;
            g.circle(cx, cy, bigR).fill({ color: 0xffffff }).stroke({ color: 0x000000, alpha: 0.65, width: Math.max(1.2, radius * 0.04) });
            g.circle(cx + lookX * 0.5, cy + lookY * 0.5, bigR * 0.52).fill({ color: 0xef4444 });
            g.circle(cx + lookX * 0.5, cy + lookY * 0.5, bigR * 0.25).fill({ color: 0x000000 });
        } else {
            for (const sign of [-1, 1]) {
                const ex = sign * eyeSpacing + lookX * 0.4;
                const ey = eyeY;
                const px = ex + Math.cos(facingAngle) * (eyeR * 0.36);
                const py = ey + Math.sin(facingAngle) * (eyeR * 0.36);

                if (cosmetics.eyes === "laser") {
                    g.rect(ex - eyeR * 1.1, ey - eyeR * 0.9, eyeR * 2.2, eyeR * 1.8)
                        .fill({ color: 0x0f172a })
                        .stroke({ color: 0xef4444, width: Math.max(1.5, radius * 0.045) });
                    g.circle(px, py, eyeR * 0.35).fill({ color: 0xef4444 });
                    g.circle(px, py, eyeR * 0.15).fill({ color: 0xffffff });
                } else if (cosmetics.eyes === "hypno") {
                    g.circle(ex, ey, eyeR).fill({ color: 0xffffff }).stroke({ color: 0x000000, width: 1.2 });
                    const spin = nowMs * 0.005;
                    for (let a = 0; a < Math.PI * 3; a += 0.4) {
                        const r = (a / (Math.PI * 3)) * (eyeR * 0.8);
                        g.circle(ex + Math.cos(a + spin) * r, ey + Math.sin(a + spin) * r, Math.max(0.8, eyeR * 0.12))
                            .fill({ color: 0x7c3aed });
                    }
                } else if (cosmetics.eyes === "cat") {
                    g.circle(ex, ey, eyeR).fill({ color: 0xfacc15 }).stroke({ color: 0x000000, width: 1.2 });
                    g.ellipse(ex + px * 0.4, ey + py * 0.4, eyeR * 0.22, eyeR * 0.72).fill({ color: 0x090d16 });
                    g.circle(ex - eyeR * 0.2, ey - eyeR * 0.2, eyeR * 0.16).fill({ color: 0xffffff });
                } else if (cosmetics.eyes === "demon") {
                    g.circle(ex, ey, eyeR).fill({ color: 0x090d16 }).stroke({ color: 0xdc2626, width: Math.max(1.2, radius * 0.04) });
                    g.circle(px, py, eyeR * 0.48).fill({ color: 0xef4444 });
                    g.ellipse(px, py, eyeR * 0.18, eyeR * 0.42).fill({ color: 0xfacc15 });
                } else if (cosmetics.eyes === "void") {
                    g.circle(ex, ey, eyeR).fill({ color: 0x030712 }).stroke({ color: 0x8b5cf6, width: Math.max(1.2, radius * 0.04) });
                    const vPulse = 0.7 + 0.3 * Math.sin(nowMs * 0.006);
                    g.circle(px, py, eyeR * 0.38).fill({ color: 0xa855f7, alpha: vPulse });
                    g.circle(px, py, eyeR * 0.16).fill({ color: 0xffffff });
                } else {
                    const scleraColor = cosmetics.eyes === "sharingan" ? 0xef4444 : cosmetics.eyes === "starry" ? 0x0f172a : 0xffffff;
                    g.circle(ex, ey, eyeR).fill({ color: scleraColor }).stroke({ color: 0x000000, alpha: 0.65, width: Math.max(1.2, radius * 0.04) });

                    if (cosmetics.eyes === "sharingan") {
                        g.circle(ex, ey, eyeR * 0.58).stroke({ color: 0x000000, width: Math.max(1, eyeR * 0.12) });
                        g.circle(px, py, eyeR * 0.28).fill({ color: 0x000000 });
                    } else if (cosmetics.eyes === "starry") {
                        g.circle(px, py, eyeR * 0.42).fill({ color: 0xfde047 });
                        g.circle(ex - eyeR * 0.25, ey - eyeR * 0.25, eyeR * 0.2).fill({ color: 0xffffff });
                    } else {
                        g.circle(px, py, eyeR * 0.46).fill({ color: 0x090d16 });
                        g.circle(px - eyeR * 0.16, py - eyeR * 0.16, eyeR * 0.18).fill({ color: 0xffffff });
                    }
                }
            }
        }

        // --- GAFAS (10) ---
        if (cosmetics.glasses === "cyber") {
            const visorW = radius * 0.78;
            const visorH = Math.max(4, radius * 0.26);
            const vx = -visorW / 2 + lookX * 0.3;
            const vy = eyeY - visorH / 2;
            g.rect(vx, vy, visorW, visorH).fill({ color: 0x38bdf8, alpha: 0.78 }).stroke({ color: 0xe0f2fe, width: Math.max(1.2, radius * 0.045) });
        } else if (cosmetics.glasses === "sunglasses") {
            const lW = eyeR * 2.1;
            const lH = eyeR * 1.5;
            g.rect(-eyeSpacing - lW / 2 + lookX * 0.3, eyeY - lH / 2, lW, lH).fill({ color: 0x090d16 }).stroke({ color: 0xe2e8f0, width: 1.2 });
            g.rect(eyeSpacing - lW / 2 + lookX * 0.3, eyeY - lH / 2, lW, lH).fill({ color: 0x090d16 }).stroke({ color: 0xe2e8f0, width: 1.2 });
            g.moveTo(-eyeSpacing + lW / 2 + lookX * 0.3, eyeY).lineTo(eyeSpacing - lW / 2 + lookX * 0.3, eyeY).stroke({ color: 0xe2e8f0, width: 1.2 });
        } else if (cosmetics.glasses === "monocle") {
            const mx = eyeSpacing + lookX * 0.4;
            g.circle(mx, eyeY, eyeR * 1.28).stroke({ color: 0xfbbf24, width: Math.max(1.8, radius * 0.065) });
            g.moveTo(mx + eyeR * 1.1, eyeY + eyeR * 0.5)
                .quadraticCurveTo(mx + eyeR * 1.2, eyeY + radius * 0.35, mx + eyeR * 0.4, eyeY + radius * 0.5)
                .stroke({ color: 0xfbbf24, width: Math.max(1, radius * 0.035) });
        } else if (cosmetics.glasses === "goggles") {
            const gW = eyeR * 2.2;
            for (const sign of [-1, 1]) {
                const gx = sign * eyeSpacing + lookX * 0.3;
                g.circle(gx, eyeY, gW * 0.55).fill({ color: 0xd97706, alpha: 0.45 }).stroke({ color: 0x78350f, width: Math.max(1.5, radius * 0.05) });
            }
            g.moveTo(-eyeSpacing + gW * 0.4, eyeY).lineTo(eyeSpacing - gW * 0.4, eyeY).stroke({ color: 0x78350f, width: Math.max(2, radius * 0.06) });
        } else if (cosmetics.glasses === "matrix") {
            const mW = eyeR * 2.0;
            const mH = eyeR * 0.9;
            g.rect(-eyeSpacing - mW / 2 + lookX * 0.3, eyeY - mH / 2, mW, mH).fill({ color: 0x022c22 }).stroke({ color: 0x10b981, width: Math.max(1.2, radius * 0.035) });
            g.rect(eyeSpacing - mW / 2 + lookX * 0.3, eyeY - mH / 2, mW, mH).fill({ color: 0x022c22 }).stroke({ color: 0x10b981, width: Math.max(1.2, radius * 0.035) });
            g.moveTo(-eyeSpacing + mW / 2 + lookX * 0.3, eyeY).lineTo(eyeSpacing - mW / 2 + lookX * 0.3, eyeY).stroke({ color: 0x10b981, width: 1.2 });
        } else if (cosmetics.glasses === "heart") {
            for (const sign of [-1, 1]) {
                const hx = sign * eyeSpacing + lookX * 0.3;
                g.circle(hx - eyeR * 0.4, eyeY - eyeR * 0.3, eyeR * 0.6).fill({ color: 0xf43f5e, alpha: 0.8 });
                g.circle(hx + eyeR * 0.4, eyeY - eyeR * 0.3, eyeR * 0.6).fill({ color: 0xf43f5e, alpha: 0.8 });
                g.poly([hx - eyeR * 0.85, eyeY - eyeR * 0.1, hx + eyeR * 0.85, eyeY - eyeR * 0.1, hx, eyeY + eyeR * 1.0], true).fill({ color: 0xf43f5e, alpha: 0.8 });
            }
        } else if (cosmetics.glasses === "nerd") {
            for (const sign of [-1, 1]) {
                const nx = sign * eyeSpacing + lookX * 0.3;
                g.circle(nx, eyeY, eyeR * 1.25).fill({ color: 0xffffff, alpha: 0.2 }).stroke({ color: 0x090d16, width: Math.max(2, radius * 0.06) });
            }
            g.rect(-radius * 0.07 + lookX * 0.3, eyeY - radius * 0.06, radius * 0.14, radius * 0.12).fill({ color: 0xffffff });
        } else if (cosmetics.glasses === "nightvision") {
            const nvW = eyeR * 1.9;
            for (const sign of [-1, 1]) {
                const nvx = sign * eyeSpacing + lookX * 0.3;
                g.circle(nvx, eyeY, nvW * 0.55).fill({ color: 0x22c55e, alpha: 0.65 }).stroke({ color: 0x14532d, width: Math.max(1.5, radius * 0.05) });
            }
            g.rect(-radius * 0.12 + lookX * 0.3, eyeY - radius * 0.04, radius * 0.24, radius * 0.08).fill({ color: 0x0f172a });
        } else if (cosmetics.glasses === "vr") {
            const vrW = radius * 0.84;
            const vrH = Math.max(6, radius * 0.34);
            const vrX = -vrW / 2 + lookX * 0.3;
            const vrY = eyeY - vrH / 2;
            g.rect(vrX, vrY, vrW, vrH).fill({ color: 0x1e293b }).stroke({ color: 0x64748b, width: Math.max(1.5, radius * 0.045) });
            const ledPulse = 0.8 + 0.2 * Math.sin(nowMs * 0.008);
            g.rect(vrX + vrW * 0.15, eyeY - radius * 0.02, vrW * 0.7, Math.max(2, radius * 0.04)).fill({ color: 0x38bdf8, alpha: ledPulse });
        }

        // --- BOCA (10) ---
        const mouthX = lookX * 0.4;
        const mouthY = radius * 0.24 + lookY * 0.3;
        const mWidth = Math.max(1.6, radius * 0.065);

        if (cosmetics.mouth === "smile") {
            g.moveTo(mouthX - radius * 0.22, mouthY - radius * 0.04)
                .quadraticCurveTo(mouthX, mouthY + radius * 0.18, mouthX + radius * 0.22, mouthY - radius * 0.04)
                .stroke({ color: 0x090d16, width: mWidth, cap: "round" });
        } else if (cosmetics.mouth === "fangs") {
            g.moveTo(mouthX - radius * 0.22, mouthY - radius * 0.04)
                .quadraticCurveTo(mouthX, mouthY + radius * 0.18, mouthX + radius * 0.22, mouthY - radius * 0.04)
                .stroke({ color: 0x090d16, width: mWidth, cap: "round" });
            for (const s of [-1, 1]) {
                const fx = mouthX + s * radius * 0.14;
                const fy = mouthY + radius * 0.06;
                g.poly([fx - radius * 0.05, fy - radius * 0.02, fx, fy + radius * 0.14, fx + radius * 0.05, fy - radius * 0.02], true)
                    .fill({ color: 0xffffff });
            }
        } else if (cosmetics.mouth === "uwu") {
            const wR = radius * 0.11;
            g.moveTo(mouthX - wR * 2, mouthY).quadraticCurveTo(mouthX - wR, mouthY + wR * 1.2, mouthX, mouthY);
            g.quadraticCurveTo(mouthX + wR, mouthY + wR * 1.2, mouthX + wR * 2, mouthY);
            g.stroke({ color: 0x090d16, width: mWidth, cap: "round" });
        } else if (cosmetics.mouth === "stitched") {
            const w = radius * 0.26;
            g.moveTo(mouthX - w, mouthY).lineTo(mouthX + w, mouthY);
            for (let t = -1; t <= 1; t++) {
                const tx = mouthX + t * (w * 0.6);
                g.moveTo(tx, mouthY - radius * 0.07).lineTo(tx, mouthY + radius * 0.07);
            }
            g.stroke({ color: 0x090d16, width: mWidth, cap: "round" });
        } else if (cosmetics.mouth === "smirk") {
            g.moveTo(mouthX - radius * 0.2, mouthY + radius * 0.02)
                .quadraticCurveTo(mouthX, mouthY + radius * 0.12, mouthX + radius * 0.26, mouthY - radius * 0.08)
                .stroke({ color: 0x090d16, width: mWidth, cap: "round" });
        } else if (cosmetics.mouth === "mustache") {
            const mPts = [
                mouthX, mouthY - radius * 0.04,
                mouthX - radius * 0.32, mouthY + radius * 0.06,
                mouthX - radius * 0.2, mouthY + radius * 0.14,
                mouthX, mouthY + radius * 0.04,
                mouthX + radius * 0.2, mouthY + radius * 0.14,
                mouthX + radius * 0.32, mouthY + radius * 0.06,
            ];
            g.poly(mPts, true).fill({ color: 0x451a03 });
        } else if (cosmetics.mouth === "grill") {
            const gW = radius * 0.42;
            const gH = radius * 0.16;
            g.rect(mouthX - gW / 2, mouthY, gW, gH).fill({ color: 0xfacc15 }).stroke({ color: 0xb45309, width: Math.max(1.2, radius * 0.04) });
            g.circle(mouthX + radius * 0.1, mouthY + radius * 0.05, Math.max(1.5, radius * 0.04)).fill({ color: 0xffffff });
        } else if (cosmetics.mouth === "vampire") {
            g.moveTo(mouthX - radius * 0.22, mouthY - radius * 0.04)
                .quadraticCurveTo(mouthX, mouthY + radius * 0.16, mouthX + radius * 0.22, mouthY - radius * 0.04)
                .stroke({ color: 0x991b1b, width: Math.max(2, radius * 0.07), cap: "round" });
            for (const s of [-1, 1]) {
                const fx = mouthX + s * radius * 0.12;
                const fy = mouthY + radius * 0.08;
                g.poly([fx - radius * 0.04, fy, fx, fy + radius * 0.16, fx + radius * 0.04, fy], true).fill({ color: 0xffffff });
            }
            g.circle(mouthX + radius * 0.12, mouthY + radius * 0.25, Math.max(1.2, radius * 0.035)).fill({ color: 0xdc2626 });
        } else if (cosmetics.mouth === "mask") {
            const mW = radius * 0.65;
            const mH = radius * 0.38;
            const maskPts = [
                mouthX - mW / 2, mouthY - radius * 0.06,
                mouthX + mW / 2, mouthY - radius * 0.06,
                mouthX + mW * 0.35, mouthY + mH,
                mouthX - mW * 0.35, mouthY + mH,
            ];
            g.poly(maskPts, true).fill({ color: 0x0f172a }).stroke({ color: 0x334155, width: Math.max(1.5, radius * 0.045) });
        }
    }

    /** Dibuja auras temáticas exteriores cromoadaptativas (10 variantes completas) */
    private drawPixiAura(g: Graphics, radius: number, aura: AuraStyle, nowMs: number, element: string = "water"): void {
        if (!Number.isFinite(radius) || radius <= 2 || !aura || aura === "none") return;
        const pal = getElementalPalette(element);

        if (aura === "crown") {
            const bob = Math.sin(nowMs * 0.005) * (radius * 0.04);
            const baseW = radius * 0.42;
            const baseY = -radius * 0.88 + bob;
            const topY = -radius * 1.28 + bob;

            const crownPts = [
                -baseW, baseY,
                -baseW * 1.1, topY + radius * 0.08,
                -baseW * 0.45, baseY - radius * 0.12,
                0, topY,
                baseW * 0.45, baseY - radius * 0.12,
                baseW * 1.1, topY + radius * 0.08,
                baseW, baseY,
            ];
            g.poly(crownPts, true)
                .fill({ color: 0xfbbf24, alpha: 0.95 })
                .stroke({ color: 0xb45309, width: Math.max(1.2, radius * 0.045) });

            // Gemas reales cromoadaptadas al elemento
            const gemR = Math.max(1.5, radius * 0.05);
            for (const gx of [-baseW * 0.7, 0, baseW * 0.7]) {
                g.circle(gx, baseY - radius * 0.05, gemR)
                    .fill({ color: pal.secondaryHex, alpha: 0.95 })
                    .stroke({ color: pal.highlightHex, width: 1 });
            }
        } else if (aura === "horns") {
            for (const s of [-1, 1]) {
                g.moveTo(s * radius * 0.35, -radius * 0.78)
                    .quadraticCurveTo(s * radius * 0.85, -radius * 1.25, s * radius * 0.48, -radius * 1.32)
                    .quadraticCurveTo(s * radius * 0.55, -radius * 0.95, s * radius * 0.62, -radius * 0.62)
                    .closePath()
                    .fill({ color: pal.primaryHex, alpha: 0.95 })
                    .stroke({ color: pal.highlightHex, width: Math.max(1.2, radius * 0.045) });
            }
        } else if (aura === "spikes") {
            const spikes = 8;
            for (let i = 0; i < spikes; i++) {
                const ang = (i / spikes) * Math.PI * 2 + nowMs * 0.001;
                const rInner = radius * 1.02;
                const rOuter = radius * 1.26;
                const pts = [
                    Math.cos(ang - 0.14) * rInner, Math.sin(ang - 0.14) * rInner,
                    Math.cos(ang) * rOuter, Math.sin(ang) * rOuter,
                    Math.cos(ang + 0.14) * rInner, Math.sin(ang + 0.14) * rInner,
                ];
                g.poly(pts, true)
                    .fill({ color: pal.secondaryHex, alpha: 0.9 })
                    .stroke({ color: pal.highlightHex, width: 1.2 });
            }
        } else if (aura === "phoenix") {
            const flutter = Math.sin(nowMs * 0.008) * (radius * 0.08);

            for (const s of [-1, 1]) {
                g.moveTo(s * radius * 1.02, -radius * 0.2)
                    .quadraticCurveTo(s * radius * 1.48, -radius * 0.95 + flutter, s * radius * 1.38, -radius * 1.45 + flutter)
                    .quadraticCurveTo(s * radius * 1.15, -radius * 0.7, s * radius * 1.05, -radius * 0.05)
                    .closePath()
                    .fill({ color: pal.secondaryHex, alpha: 0.9 })
                    .stroke({ color: pal.primaryHex, width: Math.max(1.2, radius * 0.04) });

                g.moveTo(s * radius * 1.03, -radius * 0.05)
                    .quadraticCurveTo(s * radius * 1.58, -radius * 0.35 + flutter * 0.7, s * radius * 1.48, -radius * 0.85 + flutter * 0.7)
                    .quadraticCurveTo(s * radius * 1.2, -radius * 0.4, s * radius * 1.05, 0.15 * radius)
                    .closePath()
                    .fill({ color: pal.primaryHex, alpha: 0.85 });

                g.moveTo(s * radius * 1.04, 0.1 * radius)
                    .quadraticCurveTo(s * radius * 1.38, 0.15 * radius + flutter * 0.5, s * radius * 1.28, -radius * 0.35 + flutter * 0.5)
                    .quadraticCurveTo(s * radius * 1.15, -radius * 0.1, s * radius * 1.05, 0.25 * radius)
                    .closePath()
                    .fill({ color: pal.highlightHex, alpha: 0.9 });
            }

            const crestBob = Math.sin(nowMs * 0.009) * (radius * 0.06);
            g.moveTo(-radius * 0.2, -radius * 1.02)
                .quadraticCurveTo(0, -radius * 1.5 + crestBob, 0, -radius * 1.55 + crestBob)
                .quadraticCurveTo(0, -radius * 1.5 + crestBob, radius * 0.2, -radius * 1.02)
                .quadraticCurveTo(0, -radius * 1.1, -radius * 0.2, -radius * 1.02)
                .closePath()
                .fill({ color: pal.secondaryHex, alpha: 0.9 })
                .stroke({ color: pal.highlightHex, width: Math.max(1, radius * 0.035) });
        } else if (aura === "halo") {
            const bob = Math.sin(nowMs * 0.004) * (radius * 0.05);
            const haloY = -radius * 1.05 + bob;
            g.ellipse(0, haloY, radius * 0.65, radius * 0.22).stroke({ color: pal.highlightHex, width: Math.max(2.5, radius * 0.08), alpha: 0.95 });
            g.ellipse(0, haloY, radius * 0.65, radius * 0.22).stroke({ color: pal.secondaryHex, width: Math.max(1.8, radius * 0.05), alpha: 0.95 });
            g.ellipse(0, haloY, radius * 0.65, radius * 0.22).stroke({ color: 0xffffff, width: Math.max(1, radius * 0.025), alpha: 0.98 });
        } else if (aura === "orbital_orbs") {
            const orbColors = [pal.secondaryHex, pal.primaryHex, pal.highlightHex];
            for (let i = 0; i < 3; i++) {
                const ang = nowMs * 0.002 + (i * Math.PI * 2) / 3;
                const ox = Math.cos(ang) * (radius * 1.32);
                const oy = Math.sin(ang) * (radius * 0.68) - radius * 0.1;
                const oR = Math.max(2.5, radius * 0.12);
                g.circle(ox, oy, oR * 1.4).fill({ color: orbColors[i], alpha: 0.35 });
                g.circle(ox, oy, oR).fill({ color: orbColors[i], alpha: 0.95 });
                g.circle(ox - oR * 0.35, oy - oR * 0.35, oR * 0.35).fill({ color: 0xffffff, alpha: 0.95 });
            }
        } else if (aura === "electric_storm") {
            const boltCount = 6;
            for (let b = 0; b < boltCount; b++) {
                const ang = (b / boltCount) * Math.PI * 2 + nowMs * 0.004;
                const r1 = radius * 1.05;
                const r2 = radius * 1.38 + Math.sin(nowMs * 0.01 + b) * (radius * 0.1);
                const midAng = ang + (b % 2 === 0 ? 0.2 : -0.2);
                const midR = (r1 + r2) * 0.5;

                g.moveTo(Math.cos(ang) * r1, Math.sin(ang) * r1)
                    .lineTo(Math.cos(midAng) * midR, Math.sin(midAng) * midR)
                    .lineTo(Math.cos(ang) * r2, Math.sin(ang) * r2)
                    .stroke({ color: pal.secondaryHex, width: Math.max(2.2, radius * 0.065), alpha: 0.6 });

                g.moveTo(Math.cos(ang) * r1, Math.sin(ang) * r1)
                    .lineTo(Math.cos(midAng) * midR, Math.sin(midAng) * midR)
                    .lineTo(Math.cos(ang) * r2, Math.sin(ang) * r2)
                    .stroke({ color: pal.highlightHex, width: Math.max(1.5, radius * 0.045), alpha: 0.95 });
            }
        } else if (aura === "abyssal_tendrils") {
            const tendrils = 6;
            for (let t = 0; t < tendrils; t++) {
                const baseAng = (t / tendrils) * Math.PI * 2;
                const sway = Math.sin(nowMs * 0.004 + t * 1.5) * 0.4;
                const startX = Math.cos(baseAng) * (radius * 1.05);
                const startY = Math.sin(baseAng) * (radius * 1.05);
                const endX = Math.cos(baseAng + sway) * (radius * 1.45);
                const endY = Math.sin(baseAng + sway) * (radius * 1.45);

                g.moveTo(startX, startY)
                    .quadraticCurveTo(Math.cos(baseAng + sway * 1.5) * (radius * 1.2), Math.sin(baseAng + sway * 1.5) * (radius * 1.2), endX, endY)
                    .stroke({ color: pal.primaryHex, width: Math.max(2, radius * 0.07), alpha: 0.9 });
                g.circle(endX, endY, Math.max(1.5, radius * 0.04)).fill({ color: pal.secondaryHex, alpha: 0.9 });
            }
        } else if (aura === "cosmic_nova") {
            // Vórtice Galáctico & Destellos Astrales Cromoadaptativos 🌌
            const rot = nowMs * 0.0012;
            const pulse = 1 + Math.sin(nowMs * 0.003) * 0.06;
            const novaR = radius * 1.38 * pulse;

            // 1. Doble anillo de acreción exterior ondulante
            g.circle(0, 0, novaR).stroke({ color: pal.secondaryHex, width: Math.max(1.2, radius * 0.035), alpha: 0.55 });
            g.circle(0, 0, radius * 1.12 * pulse).stroke({ color: pal.highlightHex, width: Math.max(0.8, radius * 0.02), alpha: 0.8 });

            // 2. 4 Brazos espirales galácticos fluidos
            const arms = 4;
            for (let i = 0; i < arms; i++) {
                const baseAng = (i * Math.PI * 2) / arms + rot;
                const startR = radius * 1.06;
                const midR = radius * 1.25;
                const endR = radius * 1.44;
                const x1 = Math.cos(baseAng) * startR;
                const y1 = Math.sin(baseAng) * startR;
                const cpX = Math.cos(baseAng + 0.35) * midR;
                const cpY = Math.sin(baseAng + 0.35) * midR;
                const x2 = Math.cos(baseAng + 0.65) * endR;
                const y2 = Math.sin(baseAng + 0.65) * endR;

                g.moveTo(x1, y1)
                    .quadraticCurveTo(cpX, cpY, x2, y2)
                    .stroke({ color: pal.primaryHex, width: Math.max(1.8, radius * 0.05), cap: "round" });

                // Núcleo brillante en la punta del brazo espiral
                g.circle(x2, y2, Math.max(1.5, radius * 0.035)).fill({ color: pal.highlightHex, alpha: 0.95 });
            }

            // 3. Microestrellas centelleantes de 4 puntas (Diamantes astrales)
            for (let s = 0; s < 4; s++) {
                const sAng = (s * Math.PI * 2) / 4 - rot * 0.8;
                const sR = radius * (1.18 + Math.sin(nowMs * 0.005 + s) * 0.1);
                const sx = Math.cos(sAng) * sR;
                const sy = Math.sin(sAng) * sR;
                const starSize = Math.max(2, radius * 0.05) * (0.8 + Math.sin(nowMs * 0.008 + s * 2) * 0.3);

                g.poly([
                    sx, sy - starSize,
                    sx + starSize * 0.3, sy,
                    sx, sy + starSize,
                    sx - starSize * 0.3, sy
                ], true).fill({ color: pal.highlightHex, alpha: 0.95 });

                g.poly([
                    sx - starSize, sy,
                    sx, sy + starSize * 0.3,
                    sx + starSize, sy,
                    sx, sy - starSize * 0.3
                ], true).fill({ color: pal.highlightHex, alpha: 0.95 });
            }
        }
    }

    /** 1. EFECTOS DE ESTADO LIMPIOS (CERO AROS DE IDENTIFICACIÓN REDUNDANTES) */
    private drawPixiStatusEffects(
        g: Graphics,
        player: RenderablePlayer,
        radius: number,
        playerZoom: number,
        nowMs: number,
        nowEpoch: number,
        _isLocal: boolean
    ): void {
        // Escudo protector activo
        if (player.hasShield) {
            const shieldR = radius + 6 * playerZoom;
            g.circle(0, 0, shieldR + 3 * playerZoom).stroke({ color: 0x60a5fa, width: 1.5 * playerZoom, alpha: 0.35 });
            g.circle(0, 0, shieldR).stroke({ color: 0x38bdf8, width: 2.5 * playerZoom, alpha: 0.85 });

            for (let p = 0; p < 4; p++) {
                const oAng = nowMs * 0.003 + (p * Math.PI / 2);
                const ox = Math.cos(oAng) * shieldR;
                const oy = Math.sin(oAng) * shieldR;
                g.circle(ox, oy, Math.max(1.5, 2.5 * playerZoom)).fill({ color: 0x93c5fd, alpha: 0.9 });
            }
        }

        // Cúpula dorada de invulnerabilidad activa
        if (player.invulnerableUntil > nowEpoch) {
            const pulse = 0.5 + 0.3 * Math.sin(nowMs * 0.008);
            const invulnR = radius + (7 + 3 * Math.sin(nowMs * 0.006)) * playerZoom;
            g.circle(0, 0, invulnR + 3 * playerZoom).stroke({ color: 0xfbbf24, width: 2 * playerZoom, alpha: pulse * 0.5 });
            g.circle(0, 0, invulnR).stroke({ color: 0xfef08a, width: 2.8 * playerZoom, alpha: pulse });
        }

        // Cubo de hielo de congelación activo
        if (player.frozenUntil > nowEpoch) {
            const iceR = radius + 3 * playerZoom;
            g.circle(0, 0, iceR).fill({ color: 0xa5f3fc, alpha: 0.38 });
            g.circle(0, 0, iceR).stroke({ color: 0xe0f2fe, width: 2 * playerZoom, alpha: 0.75 });

            for (let f = 0; f < 6; f++) {
                const fAng = f * (Math.PI / 3);
                const ix1 = Math.cos(fAng) * radius * 0.25;
                const iy1 = Math.sin(fAng) * radius * 0.25;
                const ix2 = Math.cos(fAng) * iceR;
                const iy2 = Math.sin(fAng) * iceR;
                g.moveTo(ix1, iy1).lineTo(ix2, iy2).stroke({ color: 0xffffff, width: 1.5, alpha: 0.65 });
            }
        }

        // 1. Poción de Velocidad (Speed Boost): líneas sónicas cian eléctrico / blanco y 3 partículas veloces hacia atrás
        if (player.speedBoostUntil > nowEpoch) {
            const rearAngle = (player.facingAngle ?? 0) + Math.PI;
            for (let k = 0; k < 3; k++) {
                const arcRadius = radius + (5 + k * 6) * playerZoom;
                const arcSpan = 0.55 + 0.12 * Math.sin(nowMs * 0.012 + k);
                const startAng = rearAngle - arcSpan;
                const endAng = rearAngle + arcSpan;
                const color = k % 2 === 0 ? 0x00e5ff : 0xffffff;
                g.arc(0, 0, arcRadius, startAng, endAng).stroke({
                    color,
                    width: 3.2 * playerZoom,
                    alpha: 0.85,
                });
            }
            // 3 partículas veloces proyectadas hacia atrás en rearAngle
            for (let p = 0; p < 3; p++) {
                const pProgress = ((nowMs * 0.006 + p * 0.33) % 1);
                const pSpread = (p - 1) * 0.28;
                const pAngle = rearAngle + pSpread;
                const pDist = radius + (4 + pProgress * 28) * playerZoom;
                const px = Math.cos(pAngle) * pDist;
                const py = Math.sin(pAngle) * pDist;
                const pSize = Math.max(1.5, (3 - pProgress * 1.8) * playerZoom);
                const pColor = p % 2 === 0 ? 0x00e5ff : 0xffffff;
                g.circle(px, py, pSize).fill({ color: pColor, alpha: (1 - pProgress) * 0.9 });
            }
        }

        // 2. Poción de Imán (Magnet) o Mascota Eléctrica: Arcos concéntricos en rotación y partículas convergentes
        const hasPetMagnet = Boolean(player.petBuffActive && player.petBuffType === "magnet_radius");
        if (player.magnetUntil > nowEpoch || hasPetMagnet) {
            const rot = nowMs * 0.005;
            const r1 = radius + 6 * playerZoom;
            const r2 = radius + 14 * playerZoom;
            const strokeCol = hasPetMagnet && player.magnetUntil <= nowEpoch ? 0xfacc15 : 0xf43f5e;
            const arcCol = hasPetMagnet && player.magnetUntil <= nowEpoch ? 0x38bdf8 : 0xfb7185;

            g.arc(0, 0, r1, rot, rot + Math.PI * 0.75).stroke({ color: strokeCol, width: 2.2 * playerZoom, alpha: 0.85 });
            g.arc(0, 0, r1, rot + Math.PI, rot + Math.PI * 1.75).stroke({ color: strokeCol, width: 2.2 * playerZoom, alpha: 0.85 });
            g.arc(0, 0, r2, -rot, -rot + Math.PI * 0.75).stroke({ color: arcCol, width: 1.8 * playerZoom, alpha: 0.75 });
            g.arc(0, 0, r2, -rot + Math.PI, -rot + Math.PI * 1.75).stroke({ color: arcCol, width: 1.8 * playerZoom, alpha: 0.75 });

            // 5 partículas de atracción orbitando y convergiendo hacia el centro del avatar
            const pCol = hasPetMagnet && player.magnetUntil <= nowEpoch ? 0xfde047 : 0xfda4af;
            for (let p = 0; p < 5; p++) {
                const pAng = (nowMs * 0.003) + (p * (Math.PI * 2 / 5));
                const pProgress = ((nowMs * 0.0018 + p * 0.2) % 1);
                const pDist = r2 - pProgress * (r2 - radius * 0.25);
                const px = Math.cos(pAng) * pDist;
                const py = Math.sin(pAng) * pDist;
                g.circle(px, py, Math.max(1.5, 2.4 * playerZoom)).fill({
                    color: pCol,
                    alpha: 0.85 * (1 - pProgress * 0.4),
                });
            }
        }

        // 3. Poción de Comida Cósmica (Cosmic Food): Halo nebuloso estelar violeta místico y 4 destellos diamantinos
        if (player.cosmicFoodUntil > nowEpoch) {
            const cosmicR = radius + 8 * playerZoom;
            const cosmicPulse = 0.65 + 0.25 * Math.sin(nowMs * 0.006);
            g.circle(0, 0, cosmicR + 4 * playerZoom).stroke({ color: 0xc084fc, width: 2 * playerZoom, alpha: cosmicPulse * 0.45 });
            g.circle(0, 0, cosmicR).stroke({ color: 0xa855f7, width: 3.2 * playerZoom, alpha: cosmicPulse });

            // 4 destellos diamantinos de 4 puntas dorado brillante rotando en coordenadas orbitales
            for (let s = 0; s < 4; s++) {
                const starAngle = nowMs * 0.003 + (s * Math.PI / 2);
                const sx = Math.cos(starAngle) * cosmicR;
                const sy = Math.sin(starAngle) * cosmicR;
                const starSize = Math.max(2.5, 4 * playerZoom);
                g.poly([
                    sx, sy - starSize,
                    sx + starSize * 0.35, sy,
                    sx, sy + starSize,
                    sx - starSize * 0.35, sy,
                ]).fill({ color: 0xfde047, alpha: 0.95 });
                g.poly([
                    sx - starSize, sy,
                    sx, sy + starSize * 0.35,
                    sx + starSize, sy,
                    sx, sy - starSize * 0.35,
                ]).fill({ color: 0xfde047, alpha: 0.95 });
            }
        }

        // 4. Poción de Renacimiento (Rebirth): Silueta de alas espirituales esmeralda translúcido con borde dorado brillante y aleteo sinusoidal
        if (player.rebirthUntil > nowEpoch) {
            const glowR = radius + 6 * playerZoom;
            const glowAlpha = 0.5 + 0.25 * Math.sin(nowMs * 0.005);
            g.circle(0, 0, glowR).stroke({ color: 0xfbbf24, width: 2.2 * playerZoom, alpha: glowAlpha });

            const flap = Math.sin(nowMs * 0.008) * 4 * playerZoom;
            // Ala izquierda
            const lwRootX = -radius * 0.6;
            const lwRootY = -radius * 0.4;
            const lwTipX = -radius * 1.4;
            const lwTipY = -radius * 1.2 + flap;
            const lwMidX = -radius * 0.7;
            const lwMidY = -radius * 0.95;
            g.poly([lwRootX, lwRootY, lwTipX, lwTipY, lwMidX, lwMidY])
                .fill({ color: 0x10b981, alpha: 0.5 })
                .stroke({ color: 0xfbbf24, width: 2.2 * playerZoom, alpha: 0.95 });

            // Ala derecha
            const rwRootX = radius * 0.6;
            const rwRootY = -radius * 0.4;
            const rwTipX = radius * 1.4;
            const rwTipY = -radius * 1.2 + flap;
            const rwMidX = radius * 0.7;
            const rwMidY = -radius * 0.95;
            g.poly([rwRootX, rwRootY, rwTipX, rwTipY, rwMidX, rwMidY])
                .fill({ color: 0x10b981, alpha: 0.5 })
                .stroke({ color: 0xfbbf24, width: 2.2 * playerZoom, alpha: 0.95 });
        }

        // 5. Bloqueo Elemental (Element Lock): Aro rúnico dorado brillante y 4 eslabones metálicos en órbita
        if (player.elementLockedUntil > nowEpoch) {
            const lockR = radius + 5 * playerZoom;
            g.circle(0, 0, lockR).stroke({ color: 0xf59e0b, width: 2.5 * playerZoom, alpha: 0.9 });

            for (let c = 0; c < 4; c++) {
                const cAng = c * (Math.PI / 2) + (nowMs * 0.0012);
                const cx = Math.cos(cAng) * lockR;
                const cy = Math.sin(cAng) * lockR;
                const linkW = 5.5 * playerZoom;
                const linkH = 2.8 * playerZoom;
                g.ellipse(cx, cy, linkW, linkH).stroke({ color: 0xfef08a, width: 1.8 * playerZoom, alpha: 0.95 });
            }
        }

        // 6. Repulsor (Repel): Si activePotionEffect === "repel", onda expansiva translúcida con 8 vectores de choque hacia afuera
        if (player.activePotionEffect === "repel") {
            const repelPulse = ((nowMs * 0.003) % 1);
            const repelR = radius + (repelPulse * 45 + 5) * playerZoom;
            const repelAlpha = (1 - repelPulse) * 0.85;
            g.circle(0, 0, repelR).stroke({ color: 0x38bdf8, width: 3 * playerZoom, alpha: repelAlpha });
            g.circle(0, 0, repelR * 0.7).stroke({ color: 0x7dd3fc, width: 1.8 * playerZoom, alpha: repelAlpha * 0.7 });

            for (let v = 0; v < 8; v++) {
                const vAng = v * (Math.PI / 4) + (nowMs * 0.002);
                const vx1 = Math.cos(vAng) * (repelR - 8 * playerZoom);
                const vy1 = Math.sin(vAng) * (repelR - 8 * playerZoom);
                const vx2 = Math.cos(vAng) * (repelR + 6 * playerZoom);
                const vy2 = Math.sin(vAng) * (repelR + 6 * playerZoom);
                g.moveTo(vx1, vy1).lineTo(vx2, vy2).stroke({ color: 0x38bdf8, width: 2.2 * playerZoom, alpha: repelAlpha });
            }
        }

        // 7. Bomba Elemental (Element Bomb): Si activePotionEffect === "element_bomb", anillo detonador prismático arcoíris con destellos elementales
        if (player.activePotionEffect === "element_bomb") {
            const bombPulse = ((nowMs * 0.0025) % 1);
            const bombR = radius + (bombPulse * 60 + 10) * playerZoom;
            const bombAlpha = (1 - bombPulse) * 0.9;
            const colors = [0xef4444, 0x38bdf8, 0x22c55e, 0xfacc15]; // Fuego, Agua, Tierra, Electricidad

            g.circle(0, 0, bombR).stroke({
                color: colors[Math.floor(nowMs * 0.01) % colors.length],
                width: 3.5 * playerZoom,
                alpha: bombAlpha,
            });
            g.circle(0, 0, bombR * 0.85).stroke({
                color: 0xffffff,
                width: 2 * playerZoom,
                alpha: bombAlpha * 0.8,
            });

            // Destellos elementales en 4 cuadrantes
            for (let b = 0; b < 4; b++) {
                const bAng = b * (Math.PI / 2) + (nowMs * 0.005);
                const bx = Math.cos(bAng) * bombR;
                const by = Math.sin(bAng) * bombR;
                const bSize = Math.max(2, 3.5 * playerZoom);
                g.circle(bx, by, bSize).fill({ color: colors[b], alpha: bombAlpha });
            }
        }

        // 8. Bebiendo Poción (Drinking 0.5s): Efervescencia con 6 burbujas ascendentes doradas y destello central
        if (player.drinkingUntil > nowEpoch) {
            // Destello central blanco/dorado
            const flashPulse = Math.sin(nowMs * 0.02);
            g.circle(0, 0, radius * 0.5).fill({ color: 0xfef08a, alpha: 0.25 + 0.15 * flashPulse });

            for (let b = 0; b < 6; b++) {
                const bSeed = ((nowMs * 0.004 + b * 0.3) % 1);
                const bAng = b * (Math.PI / 3) + bSeed * Math.PI;
                const bDist = radius * 0.45 * (1 - bSeed);
                const bx = Math.cos(bAng) * bDist;
                const by = (radius * 0.3) - (bSeed * radius * 0.85);
                const bSize = Math.max(1, (1.8 + bSeed * 2.2) * playerZoom);
                const bAlpha = (1 - bSeed) * 0.9;
                g.circle(bx, by, bSize).fill({ color: 0xfef08a, alpha: bAlpha }).stroke({ color: 0xffffff, width: 1, alpha: bAlpha });
            }
        }
    }

    /** 5. CAPA DE EFECTOS (Respawn, Muertes Explosivas, Counters, Meteoros y Pozos Gravitatorios) */
    private renderEffects(
        respawnRings: RespawnRing[],
        deaths: DeathAnimation[],
        elementalHits: ElementalHitEffect[],
        meteors: MeteorRender[],
        meteorImpacts: MeteorImpactEffect[],
        gravityCaptures: GravityCaptureEffect[],
        width: number,
        height: number,
        nowMs: number
    ): void {
        const g = this.effectsGraphics;
        g.clear();

        const zoom = this.camera.getZoom();
        const nowEpoch = Date.now();

        // 7.1. Anillos de Respawn
        for (let i = 0; i < respawnRings.length; i++) {
            const ring = respawnRings[i];
            const progress = (nowMs - ring.startTime) / ring.duration;
            if (progress < 0 || progress >= 1) continue;

            const screen = this.camera.worldToScreen(ring.x, ring.y);
            const currentRadius = (10 + progress * 70) * zoom;
            const alpha = 1 - progress;

            g.circle(screen.x, screen.y, currentRadius).stroke({
                color: ring.color,
                alpha,
                width: Math.max(1, 3 * (1 - progress) * zoom),
            });
        }

        // 5. EFECTO DE MUERTE VISIBLE Y EXPLOSIVO (Shockwave grueso 5-7px, flash blanco nuclear y 16 fragmentos veloces)
        for (let i = 0; i < deaths.length; i++) {
            const death = deaths[i];
            const elapsed = nowMs - death.startTime;
            const progress = Math.min(1, elapsed / DEATH_ANIMATION_MS);
            if (progress >= 1 || progress < 0) continue;

            const eased = 1 - Math.pow(1 - progress, 3);
            const currentRadius = death.startRadius * (1 - eased) * zoom;
            const screen = this.camera.worldToScreen(death.x, death.y);

            const maxSpread = death.startRadius * 2.8 * zoom;
            if (
                screen.x + maxSpread < 0 || screen.x - maxSpread > width ||
                screen.y + maxSpread < 0 || screen.y - maxSpread > height
            ) {
                continue;
            }

            const baseColor = ELEMENT_HEX_COLORS[death.element] ?? 0xffffff;

            // 5.1. Onda de choque expansiva brillante de 5px a 7px expandiéndose hasta 2.5x el radio inicial
            const shockRadius = death.startRadius * (0.35 + progress * 2.15) * zoom;
            const shockWidth = Math.max(3, 7 * (1 - progress) * zoom);

            g.circle(screen.x, screen.y, shockRadius).stroke({
                color: baseColor,
                width: shockWidth,
                alpha: (1 - progress) * 0.95,
            });
            g.circle(screen.x, screen.y, shockRadius * 0.96).stroke({
                color: 0xffffff,
                width: Math.max(2, 3 * (1 - progress) * zoom),
                alpha: (1 - progress) * 0.95,
            });

            // 5.2. Flash blanco nuclear central en los primeros fotogramas del impacto
            if (progress < 0.40) {
                const fT = progress / 0.40;
                const flashR = death.startRadius * (1.6 * (1 - fT) + 0.35) * zoom;
                g.circle(screen.x, screen.y, flashR * 1.35).fill({ color: baseColor, alpha: (1 - fT) * 0.70 });
                g.circle(screen.x, screen.y, flashR).fill({ color: 0xffffff, alpha: (1 - fT) * 0.98 });
            }

            // Núcleo colapsando
            if (currentRadius > 0.5) {
                g.circle(screen.x, screen.y, currentRadius).fill({ color: baseColor, alpha: 1 - eased });
                g.circle(screen.x, screen.y, currentRadius * 0.5).fill({ color: 0xffffff, alpha: (1 - eased) * 0.9 });
            }

            // 5.3. Expulsión de 16 fragmentos radiales de alta velocidad con estela luminosa
            const shardCount = 16;
            for (let s = 0; s < shardCount; s++) {
                const ang = (s / shardCount) * Math.PI * 2 + (s % 2) * 0.2;
                const dist = (death.startRadius * 0.3 + progress * death.startRadius * 2.2) * zoom;
                const sx = screen.x + Math.cos(ang) * dist;
                const sy = screen.y + Math.sin(ang) * dist;
                const shardR = Math.max(2.2, death.startRadius * 0.15 * (1 - progress) * zoom);
                const alpha = 1 - progress;

                // Estela visible y brillante hacia el centro
                const trailLen = (28 * (1 - progress) + 8) * zoom;
                g.moveTo(sx - Math.cos(ang) * trailLen, sy - Math.sin(ang) * trailLen)
                    .lineTo(sx, sy)
                    .stroke({
                        color: s % 2 === 0 ? 0xffffff : baseColor,
                        width: Math.max(2, 3.5 * (1 - progress) * zoom),
                        alpha: alpha * 0.92,
                    });

                if (death.element === "electric") {
                    g.circle(sx, sy, shardR).fill({ color: s % 2 === 0 ? 0xffffff : 0xfacc15, alpha: 0.98 });
                } else if (death.element === "earth") {
                    const diamondPts = [
                        sx, sy - shardR,
                        sx + shardR, sy,
                        sx, sy + shardR,
                        sx - shardR, sy,
                    ];
                    g.poly(diamondPts, true).fill({
                        color: s % 2 === 0 ? 0xb45309 : 0xd97706,
                        alpha: 0.98,
                    });
                } else {
                    const col = death.element === "fire" && s % 2 === 0 ? 0xfef08a : baseColor;
                    g.circle(sx, sy, shardR).fill({ color: col, alpha: 0.98 });
                    g.circle(sx, sy, shardR + 1.5).stroke({ color: 0xffffff, width: 1.2, alpha: alpha * 0.8 });
                }
            }
        }

        // 7.3. EXPLOSIONES ESPECIALIZADAS DE COUNTERS
        let damageTextIndex = 0;
        for (let i = 0; i < elementalHits.length; i++) {
            const hit = elementalHits[i];
            const elapsed = nowMs - hit.startTime;
            const progress = Math.min(1, elapsed / hit.duration);
            if (progress >= 1 || progress < 0) continue;

            const screen = this.camera.worldToScreen(hit.x, hit.y);
            if (screen.x < -100 || screen.x > width + 100 || screen.y < -100 || screen.y > height + 100) {
                continue;
            }

            const color = ELEMENTAL_HIT_HEX[hit.effectType] ?? 0xffffff;

            if (hit.effectType === "freeze-burst") {
                const radius = hit.damage * zoom;
                const ringRadius = radius * progress;
                g.circle(screen.x, screen.y, ringRadius).stroke({
                    color: 0xa5f3fc,
                    width: 4 * (1 - progress),
                    alpha: 1 - progress,
                });
                g.circle(screen.x, screen.y, ringRadius).fill({
                    color: 0xa5f3fc,
                    alpha: (1 - progress) * 0.12,
                });
                continue;
            }

            const ringRadius = (20 + progress * 40) * zoom;
            g.circle(screen.x, screen.y, ringRadius).stroke({
                color,
                width: 3 * (1 - progress) + 1,
                alpha: (1 - progress) * 0.85,
            });

            if (progress < 0.25) {
                const flashProgress = progress / 0.25;
                const flashRadius = (22 * (1 - flashProgress) + 6) * zoom;
                g.circle(screen.x, screen.y, flashRadius * 1.5).fill({ color, alpha: (1 - flashProgress) * 0.5 });
                g.circle(screen.x, screen.y, flashRadius).fill({ color: 0xffffff, alpha: (1 - flashProgress) * 0.9 });
            }

            for (let p = 0; p < 16; p++) {
                const angle = (p / 16) * Math.PI * 2 + hit.startTime * 0.001;
                const particleDistance = (p % 2 === 0 ? progress * 65 : progress * 38) * zoom;
                const px = screen.x + Math.cos(angle) * particleDistance;
                const py = screen.y + Math.sin(angle) * particleDistance;
                const size = (p % 2 === 0 ? 3.5 : 2.5) * (1 - progress) * zoom;
                if (size > 0.5) {
                    g.circle(px, py, size).fill({ color, alpha: 1 - progress });
                }
            }

            if (hit.effectType === "electric->water" || hit.effectType === "chain-electric") {
                for (let b = 0; b < 5; b++) {
                    const ang = (b / 5) * Math.PI * 2 + hit.startTime * 0.01;
                    const rMid = (18 + progress * 25) * zoom;
                    const rEnd = (32 + progress * 55) * zoom;
                    g.moveTo(screen.x, screen.y)
                        .lineTo(screen.x + Math.cos(ang + 0.18) * rMid, screen.y + Math.sin(ang + 0.18) * rMid)
                        .lineTo(screen.x + Math.cos(ang - 0.12) * rEnd, screen.y + Math.sin(ang - 0.12) * rEnd)
                        .stroke({
                            color: 0xfef08a,
                            width: Math.max(1.5, 2.5 * (1 - progress) * zoom),
                            alpha: 1 - progress,
                        });
                }
            } else if (hit.effectType === "water->fire") {
                for (let v = 0; v < 5; v++) {
                    const ang = (v / 5) * Math.PI * 2 - progress * 1.2;
                    const dist = (12 + progress * 42) * zoom;
                    const vx = screen.x + Math.cos(ang) * dist;
                    const vy = screen.y + Math.sin(ang) * dist - progress * 15 * zoom;
                    g.circle(vx, vy, (6 + progress * 8) * zoom).fill({
                        color: 0xe2e8f0,
                        alpha: (1 - progress) * 0.65,
                    });
                }
            } else if (hit.effectType === "fire->plant") {
                g.circle(screen.x, screen.y, (14 + progress * 40) * zoom).stroke({
                    color: 0xef4444,
                    width: 3 * (1 - progress),
                    alpha: 1 - progress,
                });
                g.circle(screen.x, screen.y, (8 + progress * 22) * zoom).fill({
                    color: 0xfacc15,
                    alpha: (1 - progress) * 0.8,
                });
            } else if (hit.effectType === "earth->electric") {
                g.circle(screen.x, screen.y, (12 + progress * 55) * zoom).stroke({
                    color: 0xfde68a,
                    width: 2 * (1 - progress),
                    alpha: 1 - progress,
                });
            } else if (hit.effectType === "plant->earth") {
                for (let r = 0; r < 4; r++) {
                    const rAng = (r / 4) * Math.PI * 2 + 0.2;
                    const rReach = (15 + progress * 45) * zoom;
                    g.moveTo(screen.x, screen.y)
                        .quadraticCurveTo(
                            screen.x + Math.cos(rAng + 0.3) * rReach * 0.6,
                            screen.y + Math.sin(rAng + 0.3) * rReach * 0.6,
                            screen.x + Math.cos(rAng) * rReach,
                            screen.y + Math.sin(rAng) * rReach
                        )
                        .stroke({ color: 0x22c55e, width: 2.5 * (1 - progress), alpha: 1 - progress });
                }
            } else {
                g.circle(screen.x, screen.y, (10 + progress * 28) * zoom).stroke({
                    color,
                    width: 2 * (1 - progress),
                    alpha: 1 - progress,
                });
            }

            if (hit.damage > 0) {
                let dtView: DamageTextView;
                if (damageTextIndex < this.damageTextPool.length) {
                    dtView = this.damageTextPool[damageTextIndex];
                } else {
                    dtView = new DamageTextView();
                    this.damageTextPool.push(dtView);
                    this.floatingTextsContainer.addChild(dtView.text);
                }
                damageTextIndex++;
                dtView.inUse = true;

                const textY = screen.y - 30 - progress * 30;
                const dMsg = `-${Math.round(hit.damage)}`;
                if (dtView.text.text !== dMsg) {
                    dtView.text.text = dMsg;
                }
                dtView.text.position.set(screen.x, textY);
                dtView.text.alpha = 1 - progress;
                dtView.text.visible = true;
            }
        }

        for (let k = damageTextIndex; k < this.damageTextPool.length; k++) {
            const dtView = this.damageTextPool[k];
            if (dtView.inUse) {
                dtView.clear();
            }
        }

        // METEOROS Y SOMBRAS PRE-IMPACTO
        const screenRadius = METEOR_IMPACT_RADIUS * zoom;
        for (let i = 0; i < meteors.length; i++) {
            const meteor = meteors[i];
            const screen = this.camera.worldToScreen(meteor.x, meteor.y);

            if (
                screen.x + screenRadius < -50 || screen.x - screenRadius > width + 50 ||
                screen.y + screenRadius < -50 || screen.y - screenRadius > height + 50
            ) {
                continue;
            }

            const warningStart = meteor.impactAt - METEOR_WARNING_MS;
            const t = Math.max(0, Math.min(1, (nowEpoch - warningStart) / METEOR_WARNING_MS));

            if (meteor.impacted || t >= 1) {
                g.circle(screen.x, screen.y, screenRadius).fill({ color: 0xef4444, alpha: 0.7 });
                g.circle(screen.x, screen.y, screenRadius).stroke({ color: 0xef4444, width: Math.max(1.5, 3 * zoom) });
                g.circle(screen.x, screen.y, screenRadius * 0.4).fill({ color: 0xf87171, alpha: 0.9 });
            } else {
                g.circle(screen.x, screen.y, screenRadius).fill({ color: 0x7f1d1d, alpha: 0.25 });
                g.circle(screen.x, screen.y, screenRadius).stroke({ color: 0xef4444, width: Math.max(1, 2 * zoom), alpha: 0.5 });

                const countdownRadius = screenRadius * t;
                g.circle(screen.x, screen.y, countdownRadius).fill({ color: 0xef4444, alpha: 0.35 });
                g.circle(screen.x, screen.y, countdownRadius).stroke({ color: 0xef4444, width: Math.max(1, 1.5 * zoom) });

                const crossSize = 6 * zoom;
                g.moveTo(screen.x - crossSize, screen.y).lineTo(screen.x + crossSize, screen.y)
                    .stroke({ color: 0xef4444, width: 1.5, alpha: 0.7 });
                g.moveTo(screen.x, screen.y - crossSize).lineTo(screen.x, screen.y + crossSize)
                    .stroke({ color: 0xef4444, width: 1.5, alpha: 0.7 });
            }
        }

        // EXPLOSIONES DE IMPACTO DE METEORITO
        for (let i = 0; i < meteorImpacts.length; i++) {
            const effect = meteorImpacts[i];
            const progress = Math.max(0, Math.min(1, (nowMs - effect.startTime) / effect.duration));
            if (progress >= 1 || progress < 0) continue;

            const screen = this.camera.worldToScreen(effect.x, effect.y);
            const radius = METEOR_IMPACT_RADIUS * (0.5 + progress * 1.5) * zoom;
            const alpha = 1 - progress;

            if (screen.x + radius < 0 || screen.x - radius > width || screen.y + radius < 0 || screen.y - radius > height) {
                continue;
            }

            g.circle(screen.x, screen.y, radius).stroke({
                color: progress < 0.4 ? 0xf97316 : 0xef4444,
                width: Math.max(1, (6 * (1 - progress) + 1) * zoom),
                alpha,
            });

            const flashRadius = METEOR_IMPACT_RADIUS * 0.5 * (1 - progress) * zoom;
            if (flashRadius > 0) {
                g.circle(screen.x, screen.y, flashRadius).fill({ color: 0xfef08a, alpha: alpha * 0.8 });
            }
        }

        // CAPTURAS EN POZO GRAVITATORIO
        for (let i = 0; i < gravityCaptures.length; i++) {
            const effect = gravityCaptures[i];
            const progress = Math.max(0, Math.min(1, (nowMs - effect.startTime) / effect.duration));
            if (progress >= 1 || progress < 0) continue;

            const screen = this.camera.worldToScreen(effect.x, effect.y);
            const radius = (40 + progress * 80) * zoom;
            const alpha = 1 - progress;

            if (screen.x + radius < 0 || screen.x - radius > width || screen.y + radius < 0 || screen.y - radius > height) {
                continue;
            }

            g.circle(screen.x, screen.y, radius).stroke({
                color: 0xc084fc,
                width: Math.max(1, (4 * (1 - progress) + 1) * zoom),
                alpha,
            });

            const flashRadius = 30 * (1 - progress) * zoom;
            if (flashRadius > 0) {
                g.circle(screen.x, screen.y, flashRadius).fill({ color: 0xf3e8ff, alpha: alpha * 0.75 });
            }
        }
    }

    /** TEXTOS DE COMBATE FLOTANTES (Combos, Shutdowns y Bounties) */
    private renderFloatingTexts(
        floatingTexts: FloatingCombatText[],
        width: number,
        height: number,
        nowMs: number
    ): void {
        let ftIndex = 0;

        for (let i = 0; i < floatingTexts.length; i++) {
            const ft = floatingTexts[i];
            const progress = Math.min(1, (nowMs - ft.startTime) / ft.duration);
            if (progress >= 1 || progress < 0) continue;

            const screen = this.camera.worldToScreen(ft.x, ft.y);
            const floatY = screen.y - 25 - progress * 45;

            if (screen.x < -100 || screen.x > width + 100 || floatY < -100 || floatY > height + 100) {
                continue;
            }

            const scale = progress < 0.2
                ? 0.8 + (progress / 0.2) * 0.4
                : 1.2 - (progress - 0.2) * 0.25;
            const alpha = 1 - Math.pow(progress, 2);

            let ftView: FloatingTextView;
            if (ftIndex < this.floatingTextPool.length) {
                ftView = this.floatingTextPool[ftIndex];
            } else {
                ftView = new FloatingTextView();
                this.floatingTextPool.push(ftView);
                this.floatingTextsContainer.addChild(ftView.container);
            }
            ftIndex++;
            ftView.inUse = true;

            if (ftView.text.text !== ft.text) {
                ftView.text.text = ft.text;
            }
            ftView.text.style.fill = ft.color;
            ftView.container.position.set(screen.x, floatY);
            ftView.container.scale.set(scale);
            ftView.container.alpha = alpha;
            ftView.container.visible = true;

            if (ft.subtext) {
                if (ftView.subtext.text !== ft.subtext) {
                    ftView.subtext.text = ft.subtext;
                }
                ftView.subtext.visible = true;
            } else {
                ftView.subtext.visible = false;
            }
        }

        for (let j = ftIndex; j < this.floatingTextPool.length; j++) {
            const ftView = this.floatingTextPool[j];
            if (ftView.inUse) {
                ftView.clear();
            }
        }
    }

    /**
     * 1. RESTAURAR EL MINIMAPA RADAR (renderMinimap):
     * Radar táctico en esquina inferior derecha con glassmorphism oscuro, cruz central,
     * zona de evento escalada, aro dorado Top 1, puntos elementales y halo cian local.
     */
    private renderMinimap(
        players: RenderablePlayer[],
        localSessionId: string | null,
        activeEvent: ClientEvent | null,
        width: number,
        height: number,
        nowMs: number
    ): void {
        const g = this.minimapGraphics;
        g.clear();

        const mobileControlsEl = typeof document !== "undefined" ? document.getElementById("mobile-controls") : null;
        const touchControlsVisible = Boolean(mobileControlsEl && !mobileControlsEl.classList.contains("hidden"));

        let mapSize: number;
        let mapX: number;
        let mapY: number;

        const isPrivateHudVisible = typeof document !== "undefined" && document.body.classList.contains("is-private-room");
        if (touchControlsVisible) {
            mapSize = height <= 560 ? 64 : 76;
            mapX = 10;
            mapY = isPrivateHudVisible
                ? (height <= 560 ? 74 : 92)
                : (height <= 560 ? 46 : 58);
        } else {
            mapSize = 116;
            mapX = width - mapSize - 18;
            mapY = height - mapSize - 18;
        }

        const x = mapX;
        const y = mapY;
        const cornerRadius = 8;

        // Fondo glassmorphism oscuro (rgba(11, 14, 20, 0.75))
        g.roundRect(x, y, mapSize, mapSize, cornerRadius).fill({
            color: 0x0b0e14,
            alpha: 0.75,
        });

        // Borde cian tenue (0x38bdf8, alpha: 0.35)
        g.roundRect(x, y, mapSize, mapSize, cornerRadius).stroke({
            color: 0x38bdf8,
            width: 1.2,
            alpha: 0.35,
        });

        // Cruz de coordenadas tenue en el centro
        const midX = x + mapSize / 2;
        const midY = y + mapSize / 2;
        g.moveTo(midX, y + 2).lineTo(midX, y + mapSize - 2);
        g.moveTo(x + 2, midY).lineTo(x + mapSize - 2, midY);
        g.stroke({
            color: 0xffffff,
            width: 1,
            alpha: 0.08,
        });

        // Si hay activeEvent: dibujar su zona escalada con color y pulsación
        if (activeEvent) {
            const evX = x + (activeEvent.x / this.mapWidth) * mapSize;
            const evY = y + (activeEvent.y / this.mapHeight) * mapSize;
            const evR = Math.max(3, (activeEvent.radius / this.mapWidth) * mapSize);
            const evColor = (activeEvent.color && activeEvent.color.trim().length > 0)
                ? parseColorToHex(activeEvent.color, EVENT_HEX_COLORS[activeEvent.type] ?? 0x38bdf8)
                : (EVENT_HEX_COLORS[activeEvent.type] ?? 0x38bdf8);
            const pulse = 0.5 + 0.5 * Math.sin(nowMs * 0.005);

            g.circle(evX, evY, evR).fill({
                color: evColor,
                alpha: 0.18 + pulse * 0.12,
            });
            g.circle(evX, evY, evR).stroke({
                color: evColor,
                width: 1.2,
                alpha: 0.55 + pulse * 0.25,
            });
        }

        // Identificar al Top 1 de masa entre jugadores vivos
        let top1SessionId: string | null = null;
        let maxMass = -1;
        for (let i = 0; i < players.length; i++) {
            const p = players[i];
            if (p.alive && p.mass > maxMass) {
                maxMass = p.mass;
                top1SessionId = p.sessionId;
            }
        }

        // Renderizar cada jugador vivo como un punto del color de su elemento
        for (let i = 0; i < players.length; i++) {
            const p = players[i];
            if (!p.alive) continue;

            const px = x + (p.renderX / this.mapWidth) * mapSize;
            const py = y + (p.renderY / this.mapHeight) * mapSize;

            // Restringir puntos dentro de los márgenes del minimapa
            if (px < x || px > x + mapSize || py < y || py > y + mapSize) {
                continue;
            }

            const isLocal = p.sessionId === localSessionId;
            const isTop1 = p.sessionId === top1SessionId && !isLocal;

            // Aro dorado Top 1 (0xfbbf24)
            if (isTop1) {
                g.circle(px, py, 4.2).stroke({
                    color: 0xfbbf24,
                    width: 1.2,
                    alpha: 0.95,
                });
            }

            if (isLocal) {
                // Jugador local: halo cian pulsante de 5px
                const haloR = 5.0 + Math.sin(nowMs * 0.008) * 1.5;
                g.circle(px, py, haloR).fill({
                    color: 0x38bdf8,
                    alpha: 0.40,
                });

                // Jugador local: punto blanco brillante
                g.circle(px, py, 3.2).fill({
                    color: 0xffffff,
                    alpha: 1.0,
                });
            } else {
                // Punto normal del color de su elemento
                const elemColor = ELEMENT_HEX_COLORS[p.element] ?? 0x94a3b8;
                g.circle(px, py, 2.6).fill({
                    color: elemColor,
                    alpha: 0.92,
                });
            }
        }
    }

    /** Destruye la aplicación PixiJS y libera recursos WebGL */
    public destroy(): void {
        this.isReady = false;

        for (const view of this.entityPool) {
            view.container.destroy({ children: true });
        }
        this.entityPool.length = 0;

        for (const ft of this.floatingTextPool) {
            ft.container.destroy({ children: true });
        }
        this.floatingTextPool.length = 0;

        for (const dt of this.damageTextPool) {
            dt.text.destroy();
        }
        this.damageTextPool.length = 0;

        for (const pw of this.powerUpPool) {
            pw.emojiText.destroy();
        }
        this.powerUpPool.length = 0;

        this.eventHeaderText.destroy();

        if (this.nebulaTexture) {
            this.nebulaTexture.destroy(true);
            this.nebulaTexture = null;
        }

        this.app.destroy(true, { children: true, texture: true, textureSource: true });
    }
}
